import React, { useState, useEffect, useRef } from 'react';
import { Exercise, DailyRecord, ExerciseProgram, ProgramDay } from '../types';
import { loadAppState, saveAppState, toPersianDigits } from '../lib/storage';
import { stripMarkdownAndSummarize } from '../lib/workerAi';
import { getStoredUser, apiGet, apiPost, apiPut, apiDelete } from '../lib/api';
import { saveFileRecordToCloud, deleteCloudFileRecord, detectFileType } from '../lib/cloudFileStorage';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { getGuidedWorkoutPhrases, normalizeExerciseDigits } from '../lib/exerciseTranslations';
import { isUserAdmin } from '../lib/versionHistory';
import { 
  Activity, Play, Pause, Square, SkipForward, SkipBack, 
  CheckCircle2, AlertCircle, Timer, Sparkles, ChevronDown, ChevronUp, 
  Volume2, VolumeX, Info, RefreshCw, Plus, Trash2, Inbox, Sliders, ShieldAlert,
  Video, Image, Film, UploadCloud, X, Edit3, Camera, Paperclip, Link, Folder, FileCode,
  Layers, Dumbbell, Globe, User, Copy, PlusCircle, Eye, Lock
} from 'lucide-react';
import AiChatBox from './AiChatBox';
import SharedFileManager from './SharedFileManager';
import BulkExerciseImportModal from './BulkExerciseImportModal';
import VoiceInputButton from './VoiceInputButton';
import ProgramListManager from './ProgramListManager';
import ScheduleProgramDayModal from './ScheduleProgramDayModal';
import ExerciseDetailModal from './ExerciseDetailModal';
import AddToProgramModal from './AddToProgramModal';
import { parseVideoMetadata, getEmbedVideoUrl } from '../lib/videoUtils';

function ExerciseMediaViewer({ exercise, className = '' }: { exercise: Exercise; className?: string }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'video' | 'image'>(
    exercise.videoUrl ? 'video' : 'image'
  );

  const videoMeta = exercise.videoUrl ? parseVideoMetadata(exercise.videoUrl) : null;
  const embedUrl = videoMeta?.embedUrl || (exercise.videoUrl ? getEmbedVideoUrl(exercise.videoUrl) : null);
  const isDirectVideo = Boolean(
    videoMeta?.isDirectVideo || 
    (exercise.videoUrl && (
      exercise.videoUrl.startsWith('data:video') || 
      exercise.videoUrl.startsWith('blob:') ||
      exercise.videoUrl.endsWith('.mp4') || 
      exercise.videoUrl.endsWith('.webm') || 
      exercise.videoUrl.endsWith('.mov') ||
      exercise.videoUrl.includes('/api/files/')
    ))
  );
  const isInstagram = videoMeta?.provider === 'instagram' || Boolean(exercise.videoUrl && (
    exercise.videoUrl.includes('instagram.com') || exercise.videoUrl.includes('instagr.am')
  ));

  if (!exercise.videoUrl && !exercise.image) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Switcher if both video and image are present */}
      {exercise.videoUrl && exercise.image && (
        <div className="flex items-center justify-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => setActiveTab('video')}
            className={`px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'video'
                ? 'bg-purple-600 text-white shadow-xs'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
            }`}
          >
            <Video className="w-3.5 h-3.5" />
            {t('rehabWatchVideo', 'مشاهده فیلم آموزشی')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('image')}
            className={`px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'image'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
            }`}
          >
            <Image className="w-3.5 h-3.5" />
            {t('rehabViewImage', 'مشاهده تصویر حرکت')}
          </button>
        </div>
      )}

      {/* Video View */}
      {(activeTab === 'video' || !exercise.image) && exercise.videoUrl && (
        <div className="overflow-hidden rounded-2xl border border-purple-200 dark:border-purple-900 bg-black shadow-md flex flex-col items-center">
          {embedUrl ? (
            <div className={`relative w-full ${isInstagram ? 'h-[500px] max-w-sm mx-auto' : 'aspect-video'}`}>
              <iframe
                src={embedUrl}
                title={exercise.name}
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : isDirectVideo ? (
            <video
              src={exercise.videoUrl}
              controls
              controlsList="nodownload"
              preload="metadata"
              className="w-full max-h-80 object-contain mx-auto"
            />
          ) : (
            <div className="p-4 text-center bg-gray-900 w-full space-y-2">
              <a
                href={exercise.videoUrl.startsWith('http') ? exercise.videoUrl : `https://${exercise.videoUrl}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition-all shadow-md"
              >
                <Video className="w-4 h-4" />
                {t('rehabPlayVideoLink', 'مشاهده و پخش لینک ویدیو (اینستاگرام / وب)')}
              </a>
            </div>
          )}

          {isInstagram && (
            <div className="p-2.5 bg-gray-950 w-full text-center border-t border-gray-800 flex items-center justify-between px-4">
              <span className="text-[11px] text-gray-400 font-semibold">{t('rehabInstagramPost', 'پست / ریلز اینستاگرام')}</span>
              <a
                href={exercise.videoUrl.startsWith('http') ? exercise.videoUrl : `https://${exercise.videoUrl}`}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-purple-400 hover:text-purple-300 font-bold inline-flex items-center gap-1 hover:underline"
              >
                <span>{t('rehabOpenInInstagram', 'باز کردن مستقیم در اپ اینستاگرام')}</span>
                <Link className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
        </div>
      )}

      {/* Image View */}
      {(activeTab === 'image' || !exercise.videoUrl) && exercise.image && (
        <div className="flex justify-center items-center py-2 bg-gray-50/50 dark:bg-gray-900/40 rounded-2xl border border-gray-200 dark:border-gray-700">
          <img
            src={exercise.image}
            alt={exercise.name}
            className="max-h-80 object-contain rounded-xl shadow-sm"
            referrerPolicy="no-referrer"
          />
        </div>
      )}
    </div>
  );
}

interface RehabProps {
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
  themeAccent?: string;
}

const normalizePersianText = (str: string): string => {
  if (!str) return "";
  return str
    .replace(/[\u064a\u0649]/g, "\u06cc") // Arabic YEH to Persian YEH
    .replace(/[\u0643]/g, "\u06a9")     // Arabic KAF to Persian KAF
    .replace(/[\u0610-\u061a\u064b-\u065f\u0670]/g, "") // Remove diacritics
    .toLowerCase()
    .trim();
};

const getLocalRehabExerciseFallback = (painDescription: string) => {
  const normalizedInput = normalizePersianText(painDescription);
  
  const sciaticKeywords = [
    "سیاتیک", "sciatic", "پشت پا", "پشت پای", "زدن به پا", "تیر کشیدن", "تیر بکشد", "تیر میکشد", "باسن", "ران"
  ].map(k => normalizePersianText(k));

  const lumbarKeywords = [
    "نشستن", "sitting", "کمر", "کمردرد", "گرفتگی", "خشکی", "دیسک", "ستون فقرات", "مهره", "شانه", "پشت", "درد کمر", "درد شدید"
  ].map(k => normalizePersianText(k));

  const neckKeywords = [
    "گردن", "neck", "کتف", "سردرد", "بالای پشت", "سر درد", "گردن درد"
  ].map(k => normalizePersianText(k));

  const shoulderKeywords = [
    "شانه", "shoulder", "دست", "بال تنه", "بازو", "ترقوه"
  ].map(k => normalizePersianText(k));

  const kneeKeywords = [
    "زانو", "knee", "مفصل", "کشکک", "مینیسک", "رباط"
  ].map(k => normalizePersianText(k));

  // 1. Check sciatic
  if (sciaticKeywords.some(keyword => normalizedInput.includes(keyword))) {
    return {
      name: "عصب‌کشی ملایم سیاتیک (Sciatic Nerve Flossing)",
      category: "rehab" as const,
      description: "به پشت دراز بکشید. ران پای دردناک خود را با دست‌ها در حالت ۹۰ درجه نگه دارید. به آرامی زانو را صاف کنید تا پنجه پا رو به سقف قرار گیرد. سپس مچ پا را به آرامی به سمت صورت خم کنید و رها کنید. این چرخه رفت و برگشت را به صورت کنترل‌شده و بدون احساس کشش شدید انجام دهید.",
      purpose: "متحرک‌سازی و رفع چسبندگی عصب سیاتیک در مسیر عبور از ستون فقرات و عضلات همسترینگ بدون ایجاد التهاب.",
      targetMuscles: "عصب سیاتیک، همسترینگ، دوقلوی ساق پا",
      sets: 3,
      reps: "۱۰ تکرار بسیار روان (بدون مکث در اوج)",
      rest: "۳۰ ثانیه",
      duration: 3,
      difficulty: "Beginner" as const
    };
  }

  // 2. Check neck
  if (neckKeywords.some(keyword => normalizedInput.includes(keyword))) {
    return {
      name: "تمرین عقب کشیدن چانه (Chin Tuck)",
      category: "rehab" as const,
      description: "صاف بنشینید و به جلو نگاه کنید. بدون اینکه سر خود را به بالا یا پایین خم کنید، چانه خود را مستقیماً به سمت عقب بکشید (انگار می‌خواهید غبغب ایجاد کنید). این وضعیت را برای ۵ ثانیه نگه دارید و سپس رها کنید.",
      purpose: "اصلاح وضعیت سر به جلو (Forward Head Posture) ناشی از کار با موبایل و کامپیوتر و کاهش فشار روی دیسک‌های گردنی.",
      targetMuscles: "عضلات عمیق فلکسور گردن، ذوزنقه‌ای بالایی",
      sets: 3,
      reps: "۱۰ تکرار، مکث ۵ ثانیه",
      rest: "۲۰ ثانیه",
      duration: 3,
      difficulty: "Beginner" as const
    };
  }

  // 3. Check shoulder
  if (shoulderKeywords.some(keyword => normalizedInput.includes(keyword))) {
    return {
      name: "تمرین کشش شانه با دیوار (Wall Shoulder Stretch)",
      category: "rehab" as const,
      description: "در کنار یک دیوار بایستید. آرنج و ساعد خود را با زاویه ۹۰ درجه روی دیوار قرار دهید. به آرامی بدن خود را به سمت مخالف بچرخانید تا زمانی که کشش ملایمی در جلوی شانه و قفسه سینه احساس کنید. ۳۰ ثانیه نگه دارید.",
      purpose: "باز کردن قفسه سینه و کاهش وضعیت شانه افتاده به جلو ناشی از کار با کامپیوتر.",
      targetMuscles: "عضله سینه‌ای بزرگ، دلتوئید قدامی",
      sets: 3,
      reps: "۳ تکرار، ۳۰ ثانیه مکث برای هر طرف",
      rest: "۲۰ ثانیه",
      duration: 4,
      difficulty: "Beginner" as const
    };
  }

  // 4. Check knee
  if (kneeKeywords.some(keyword => normalizedInput.includes(keyword))) {
    return {
      name: "انقباض ایزومتریک چهارسر ران (Quad Sets)",
      category: "rehab" as const,
      description: "روی زمین بنشینید و پاهای خود را دراز کنید. یک حوله لوله‌شده کوچک را زیر زانوی خود قرار دهید. عضلات جلوی ران خود را منبسط و منقبض کرده و زانو را به سمت حوله فشار دهید. ۵ ثانیه نگه دارید و رها کنید.",
      purpose: "تقویت عضله چهارسر ران برای حمایت از مفصل زانو و کاهش ساییدگی و درد کشکک.",
      targetMuscles: "عضله چهارسر ران (پهن داخلی)",
      sets: 3,
      reps: "۱۰ تکرار، ۵ ثانیه مکث",
      rest: "۱۵ ثانیه",
      duration: 3,
      difficulty: "Beginner" as const
    };
  }

  // 5. Check lumbar/sitting
  if (lumbarKeywords.some(keyword => normalizedInput.includes(keyword))) {
    return {
      name: "کوشش پسواس خفته (Supine Psoas & Hip Flexor Stretch)",
      category: "rehab" as const,
      description: "لبه یک تخت سفت دراز بکشید. یک زانو را در آغوش بگیرید و به سمت قفسه سینه بکشید. بگذارید پای دیگر به آرامی از لبه تخت به پایین آویزان شود. با نفس‌های عمیق اجازه دهید وزن پا عضله خم‌کننده باسن را منبسط کند.",
      purpose: "آزاد کردن عضله پسواس بزرگ (Psoas Major) که به علت نشستن طولانی‌مدت منقبض شده و دیسک‌های کمری را به جلو می‌کشد.",
      targetMuscles: "عضله پسواس بزرگ، راست رانی، خم‌کننده‌های ران",
      sets: 3,
      reps: "۳۰ ثانیه مکث برای هر طرف",
      rest: "۲۰ ثانیه",
      duration: 4,
      difficulty: "Beginner" as const
    };
  }

  // 6. Absolute default
  return {
    name: "تمرین تراکشن کمری غیرفعال (Passive Lumbar Traction)",
    category: "rehab" as const,
    description: "به پشت بخوابید و ساق پارهای خود را روی یک صندلی یا مبل قرار دهید به طوری که زاویه باسن و زانو هر دو ۹۰ درجه باشد. دست‌ها را در کنار خود باز کنید و برای ۵ دقیقه فقط نفس عمیق شکمی بکشید و بگذارید جاذبه کمر را باز کند.",
    purpose: "کاهش فشارهای فشردگی (Decompression) روی مهره‌های کمری و تسکین مستقیم ریشه‌های عصبی ملتهب دیسک.",
    targetMuscles: "راست‌کننده ستون فقرات، عضلات خلفی کمر",
    sets: 1,
    reps: "۵ دقیقه استراحت مطلق شکمی",
    rest: "بدون استراحت",
    duration: 5,
    difficulty: "Beginner" as const
  };
};

export default function RehabModule({ record, onUpdateRecord, themeAccent = 'indigo' }: RehabProps) {
  const { lang, t } = useLanguage();
  const getAccentBgClass = (accent?: string) => {
    switch (accent) {
      case 'emerald':
        return 'bg-emerald-600 hover:bg-emerald-700 text-white';
      case 'amber':
        return 'bg-amber-500 hover:bg-amber-600 text-white';
      case 'rose':
        return 'bg-rose-600 hover:bg-rose-700 text-white';
      case 'violet':
        return 'bg-violet-600 hover:bg-violet-700 text-white';
      case 'sky':
        return 'bg-sky-500 hover:bg-sky-600 text-white';
      case 'indigo':
      default:
        return 'bg-indigo-600 hover:bg-indigo-700 text-white';
    }
  };

  // Load initial state synchronously for immediate render safety
  const [rehabExercises, setRehabExercises] = useState<Exercise[]>([]);

  const [rehabInbox, setRehabInbox] = useState<{ id: string; exercise: Exercise; timestamp: string; status: 'pending' | 'accepted' | 'declined' }[]>(() => {
    try {
      const appState = loadAppState();
      return appState.settings.rehabInbox || [];
    } catch (e) {
      return [];
    }
  });

  // Multi-day Programs State
  const [exercisePrograms, setExercisePrograms] = useState<ExerciseProgram[]>(() => {
    try {
      const appState = loadAppState();
      return appState.settings.exercisePrograms || [];
    } catch (e) {
      return [];
    }
  });

  const [activeProgramId, setActiveProgramId] = useState<string | undefined>(() => {
    try {
      const appState = loadAppState();
      return appState.settings.activeProgramId;
    } catch (e) {
      return undefined;
    }
  });

  const [rehabSubTab, setRehabSubTab] = useState<'programs' | 'library'>('programs');
  const [schedulingDayInfo, setSchedulingDayInfo] = useState<{ day: ProgramDay; program: ExerciseProgram } | null>(null);

  // Global vs Personal Library filtering and modals
  const [libraryFilterScope, setLibraryFilterScope] = useState<'all' | 'global' | 'personal'>('all');
  const [libraryCategoryFilter, setLibraryCategoryFilter] = useState<'all' | 'rehab' | 'fat-loss'>('all');
  const [selectedDetailExercise, setSelectedDetailExercise] = useState<Exercise | null>(null);
  const [exerciseForAddToProgram, setExerciseForAddToProgram] = useState<Exercise | null>(null);
  const [copyingExerciseId, setCopyingExerciseId] = useState<string | null>(null);
  const [publishingExerciseId, setPublishingExerciseId] = useState<string | null>(null);
  const [showPublishAllModal, setShowPublishAllModal] = useState(false);
  const [isPublishingAll, setIsPublishingAll] = useState(false);
  const [publishAllResults, setPublishAllResults] = useState<{
    summary: { total: number; published: number; alreadyPublished: number; failed: number };
    details: Array<{ id: string; name: string; status: 'published' | 'already_published' | 'failed'; globalId?: string; error?: string }>;
  } | null>(null);

  const storedUser = getStoredUser();
  const isGlobalPublisherAdmin = isUserAdmin(storedUser?.email);

  const openEditModal = (ex: Exercise) => {
    setEditingExercise({
      ...ex,
      name: ex.name || '',
      category: ex.category || 'rehab',
      description: ex.description || '',
      purpose: ex.purpose || (ex as any).goal || '',
      targetMuscles: ex.targetMuscles || (ex as any).target_muscles || (ex as any).muscles || '',
      sets: Number(ex.sets ?? 3),
      reps: String(ex.reps ?? '10'),
      rest: String(ex.rest ?? (ex as any).rest_time ?? '20'),
      duration: Number(ex.duration ?? (ex as any).duration_minutes ?? 5),
      difficulty: ex.difficulty || 'Beginner',
      image: ex.image || (ex as any).image_url || undefined,
      videoUrl: ex.videoUrl || (ex as any).video_url || undefined,
    });
  };

  const handleCreatePersonalCopy = async (ex: Exercise, autoEdit = false) => {
    setCopyingExerciseId(ex.id);
    try {
      const res = await apiPost(`/api/rehab/exercises/${ex.id}/copy`, {});
      if (res.success && res.data) {
        const cloned: Exercise = {
          id: res.data.id,
          user_id: res.data.user_id,
          scope: 'personal',
          name: res.data.name,
          category: res.data.category || 'rehab',
          description: res.data.description || '',
          purpose: res.data.purpose || '',
          targetMuscles: res.data.target_muscles || '',
          precautions: res.data.precautions || '',
          equipment: res.data.equipment || '',
          sets: Number(res.data.sets || 3),
          reps: String(res.data.reps || '10'),
          rest: String(res.data.rest_time || '20'),
          duration: Number(res.data.duration_minutes || 5),
          difficulty: res.data.difficulty || 'Beginner',
          image: res.data.image_url || null,
          videoUrl: res.data.video_url || null,
          isCustom: true
        };
        const updated = [cloned, ...rehabExercises];
        setRehabExercises(updated);
        saveRehabData(updated, rehabInbox);
        if (autoEdit) {
          openEditModal(cloned);
        } else {
          alert(`نسخه شخصی تمرین "${cloned.name}" با موفقیت ایجاد شد و در بانک حرکات شما قرار گرفت.`);
        }
      } else {
        throw new Error(res?.error || 'خطا در ایجاد نسخه شخصی');
      }
    } catch (err: any) {
      alert(err?.message || 'خطا در ایجاد نسخه شخصی تمرین.');
    } finally {
      setCopyingExerciseId(null);
    }
  };

  const handlePublishToGlobal = async (ex: Exercise) => {
    if (!isGlobalPublisherAdmin) {
      alert(t('publishToGlobalForbidden', 'تنها حساب کاربری مدیریت (salim.zarepour@gmail.com) مجاز به انتشار تمرینات در بانک عمومی است.'));
      return;
    }

    const confirmMsg = t('publishToGlobalConfirm', 'آیا از انتشار این تمرین در بانک عمومی دیتابیس D1 اطمینان دارید؟ پس از انتشار، این تمرین برای همه کاربران به صورت عمومی و استاندارد در دسترس خواهد بود.');
    let isConfirmed = true;
    try {
      if (typeof window !== 'undefined' && window.confirm) {
        isConfirmed = window.confirm(confirmMsg);
      }
    } catch {
      isConfirmed = true;
    }
    if (!isConfirmed) return;

    setPublishingExerciseId(ex.id);
    try {
      const res = await apiPost(`/api/rehab/exercises/${ex.id}/publish-global`, {});
      if (res.success && res.data) {
        await refreshExercisesFromCloud();
        const successMsg = t('publishToGlobalSuccess', 'تمرین «{{name}}» با موفقیت در بانک حرکات عمومی دیتابیس D1 منتشر شد.', { name: res.data.name || ex.name });
        alert(successMsg);
      } else {
        throw new Error(res?.error || t('publishToGlobalGenericError', 'خطا در انتشار تمرین در بانک عمومی'));
      }
    } catch (err: any) {
      alert(t('publishToGlobalError', 'خطا در انتشار تمرین در بانک عمومی: {{err}}', { err: err?.message || err }));
    } finally {
      setPublishingExerciseId(null);
    }
  };

  const handlePublishAllToGlobal = async () => {
    if (!isGlobalPublisherAdmin) {
      alert(t('publishToGlobalForbidden', 'تنها حساب کاربری مدیریت (salim.zarepour@gmail.com) مجاز به انتشار تمرینات در بانک عمومی است.'));
      return;
    }
    setIsPublishingAll(true);
    setPublishAllResults(null);
    try {
      const res: any = await apiPost('/api/rehab/exercises/publish-all-global', {});
      if (res && res.success && res.summary) {
        setPublishAllResults({
          summary: res.summary,
          details: res.details || []
        });
        await refreshExercisesFromCloud();
      } else {
        throw new Error(res?.error || t('publishToGlobalGenericError', 'خطا در انتشار دسته‌جمعی تمرینات در بانک عمومی'));
      }
    } catch (err: any) {
      alert(t('publishToGlobalError', 'خطا در انتشار تمرین در بانک عمومی: {{err}}', { err: err?.message || err }));
    } finally {
      setIsPublishingAll(false);
    }
  };

  useEffect(() => {
    apiGet('/api/rehab/programs').then((res) => {
      if (res && res.success && Array.isArray(res.data)) {
        setExercisePrograms(res.data);
        const active = res.data.find((p: any) => p.isActive);
        if (active) {
          setActiveProgramId(active.id);
        }
        const appState = loadAppState();
        appState.settings.exercisePrograms = res.data;
        if (active) appState.settings.activeProgramId = active.id;
        saveAppState(appState, false);
      }
    }).catch(() => {});
  }, []);

  const startGuidedSessionForDay = (day: ProgramDay, program: ExerciseProgram) => {
    if (!day.exercises || day.exercises.length === 0) {
      alert(t('rehabNoExercisesInDay', 'این روز تمرینی هیچ حرکتی ندارد.'));
      return;
    }

    const convertedExercises: Exercise[] = day.exercises.map((ex, idx) => ({
      id: ex.exerciseId || `prog_ex_${idx}_${Date.now()}`,
      name: ex.name,
      category: ex.category || 'rehab',
      description: ex.description || '',
      purpose: ex.purpose || '',
      targetMuscles: ex.targetMuscles || '',
      sets: ex.sets || 3,
      reps: ex.reps || '10',
      rest: ex.rest || '20 ثانیه',
      duration: ex.duration || 5,
      difficulty: ex.difficulty || 'Beginner',
      image: ex.image,
      videoUrl: ex.videoUrl
    }));

    setRehabExercises(convertedExercises);
    setIsGuidedMode(true);
    setCurrentIdx(0);
    setSessionStatus('prepare');
    setSecondsRemaining(8);
    setIsPaused(false);
  };

  // Bulk Import Modal State
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [isSyncingCloud, setIsSyncingCloud] = useState(false);

  // Custom Exercise Creator Form States
  const [showAddCustomForm, setShowAddCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customPurpose, setCustomPurpose] = useState('');
  const [customTargetMuscles, setCustomTargetMuscles] = useState('');
  const [customSets, setCustomSets] = useState(3);
  const [customReps, setCustomReps] = useState('۱۰ تکرار');
  const [customRest, setCustomRest] = useState('۲۰ ثانیه');
  const [customDuration, setCustomDuration] = useState(3);
  const [customDifficulty, setCustomDifficulty] = useState<'Beginner' | 'Intermediate' | 'Advanced'>('Beginner');
  const [customImage, setCustomImage] = useState<string | null>(null);
  const [customVideoUrl, setCustomVideoUrl] = useState<string | null>(null);

  // Edit Exercise Modal State
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);

  // Video File Handler
  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>, callback: (url: string | null) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      alert("آپلود مستقیم فایل ویدیو به علت حجم بالا مجاز نیست.\nلطفاً از بخش لینک ویدیو برای قرار دادن لینک مستقیم یا لینک یوتیوب/آپارات استفاده نمایید.");
      e.target.value = '';
    }
  };

  // User Profile Intake Form States for Rehab AI
  const [painLocation, setPainLocation] = useState(t('rehabDefaultPainLocation', 'کمر و ستون فقرات'));
  const [painLevel, setPainLevel] = useState(5);
  const [limitations, setLimitations] = useState(t('rehabDefaultLimitations', 'محدودیت در نشستن طولانی و خم شدن'));
  const [equipment, setEquipment] = useState(t('rehabDefaultEquipment', 'مت یوگا، بدون تجهیزات خاص'));
  const [experience, setExperience] = useState('Beginner');

  // Keep intake defaults synchronized with active language
  useEffect(() => {
    const defaultPainLocations = ['کمر و ستون فقرات', 'Lower back & spine', 'Ländrygg & ryggrad'];
    if (defaultPainLocations.includes(painLocation)) {
      setPainLocation(t('rehabDefaultPainLocation', 'کمر و ستون فقرات'));
    }
    const defaultLimits = ['محدودیت در نشستن طولانی و خم شدن', 'Limited prolonged sitting and bending', 'Begränsning vid långvarigt sittande och böjning'];
    if (defaultLimits.includes(limitations)) {
      setLimitations(t('rehabDefaultLimitations', 'محدودیت در نشستن طولانی و خم شدن'));
    }
    const defaultEqs = ['مت یوگا، بدون تجهیزات خاص', 'Yoga mat, no special equipment', 'Yogamatta, ingen specialutrustning'];
    if (defaultEqs.includes(equipment)) {
      setEquipment(t('rehabDefaultEquipment', 'مت یوگا، بدون تجهیزات خاص'));
    }
  }, [lang]);

  // Cloud refresh helper from Cloudflare D1
  const refreshExercisesFromCloud = async () => {
    setIsSyncingCloud(true);
    try {
      const res = await apiGet('/api/rehab/exercises');
      if (res.success && Array.isArray(res.data)) {
        const exercises: Exercise[] = res.data.map((r: any) => ({
          id: r.id,
          user_id: r.user_id,
          scope: r.scope || (r.user_id ? 'personal' : 'global'),
          name: r.name,
          category: r.category || 'rehab',
          description: r.description || '',
          purpose: r.purpose || '',
          targetMuscles: r.targetMuscles || r.target_muscles || '',
          precautions: r.precautions || '',
          equipment: r.equipment || '',
          sets: Number(r.sets ?? 3),
          reps: r.reps !== null && r.reps !== undefined ? String(r.reps) : '10',
          rest: r.rest !== null && r.rest !== undefined ? String(r.rest) : (r.rest_time !== null && r.rest_time !== undefined ? String(r.rest_time) : '20'),
          duration: Number(r.duration || r.duration_minutes || 5),
          difficulty: r.difficulty || 'Beginner',
          image: r.image || r.image_url || null,
          videoUrl: r.videoUrl || r.video_url || null,
          isCustom: Boolean(r.isCustom || r.is_custom || r.user_id),
          source_exercise_id: r.source_exercise_id || r.sourceExerciseId || null,
          linked_global_id: r.linked_global_id || r.linkedGlobalId || null,
          isPublishedToGlobal: Boolean(r.is_published_to_global || r.isPublishedToGlobal || r.linked_global_id)
        }));
        setRehabExercises(exercises);
      }
    } catch (err) {
      console.warn('Could not refresh exercises from cloud API:', err);
    } finally {
      setIsSyncingCloud(false);
    }
  };

  const handleApplyParsedRehabExercise = async (parsed: any) => {
    const cleanName = stripMarkdownAndSummarize(parsed.name || 'تمرین کششی توانبخشی', 60);
    const cleanDesc = stripMarkdownAndSummarize(parsed.description || 'تمرین کششی توصیه شده هوش مصنوعی', 220);
    const cleanPurpose = stripMarkdownAndSummarize(parsed.purpose || 'تسکین درد و بهبود انعطاف‌پذیری', 100);
    const cleanTarget = stripMarkdownAndSummarize(parsed.targetMuscles || painLocation, 80);

    const newEx: Exercise = {
      id: `rehab_ai_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      name: cleanName,
      category: 'rehab',
      description: cleanDesc,
      purpose: cleanPurpose,
      targetMuscles: cleanTarget,
      sets: parsed.sets || 3,
      reps: parsed.reps || '۱۰ تکرار',
      rest: parsed.rest || '۲۰ ثانیه',
      duration: parsed.duration || 4,
      difficulty: (parsed.difficulty as any) || 'Beginner',
      isCustom: true
    };

    try {
      await apiPost('/api/rehab/exercises', {
        id: newEx.id,
        module_id: 'module_1_rehab',
        name: newEx.name,
        category: newEx.category,
        description: newEx.description,
        purpose: newEx.purpose,
        target_muscles: newEx.targetMuscles,
        sets: newEx.sets,
        reps: newEx.reps,
        rest_time: newEx.rest,
        duration_minutes: newEx.duration,
        difficulty: newEx.difficulty,
        is_custom: 1
      });
    } catch (err) {
      console.warn('API save error for AI exercise:', err);
    }

    const updated = [...rehabExercises, newEx];
    saveRehabData(updated, rehabInbox);
    alert(`تمرین "${newEx.name}" با موفقیت در دیتابیس ثبت و به لیست تمرینات شما اضافه شد.`);
  };

  const compressAndSetImage = (file: File, callback: (base64: string | null) => void) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const maxDim = 800;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          const compressed = canvas.toDataURL('image/jpeg', 0.7);
          callback(compressed);
        } else {
          callback(reader.result as string);
        }
      };
      img.onerror = () => callback(reader.result as string);
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      compressAndSetImage(file, (dataUrl) => setCustomImage(dataUrl));
    } else {
      setCustomImage(null);
    }
  };

  // AI Generation States
  const [aiPainInput, setAiPainInput] = useState('');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Sync initial state from D1 cloud
  useEffect(() => {
    const appState = loadAppState();
    const inbox = appState.settings.rehabInbox || [];
    setRehabInbox(inbox);

    // Refresh from D1 cloud (ONLY source of truth)
    refreshExercisesFromCloud();
  }, []);

  // Save updates helper
  const saveRehabData = async (newExercises: Exercise[], newInbox: typeof rehabInbox) => {
    setRehabExercises(newExercises);
    setRehabInbox(newInbox);
    const appState = loadAppState();
    appState.settings.rehabInbox = newInbox;
    saveAppState(appState);
  };

  const handleAddCustomExercise = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customName.trim() || !customDescription.trim()) return;

    const newEx: Exercise = {
      id: `rehab_custom_${Date.now()}`,
      scope: 'personal',
      name: stripMarkdownAndSummarize(customName.trim(), 60),
      category: 'rehab',
      description: stripMarkdownAndSummarize(customDescription.trim(), 220),
      purpose: stripMarkdownAndSummarize(customPurpose.trim() || 'بهبود دایره حرکتی مفاصل و رفع اسپاسم عضلات حامی دیسک کمر', 100),
      targetMuscles: stripMarkdownAndSummarize(customTargetMuscles.trim() || 'عضلات فیله و ثبات‌دهنده کور', 80),
      precautions: '',
      equipment: '',
      sets: Number(customSets) || 3,
      reps: customReps.trim() || '۱۰ تکرار',
      rest: customRest.trim() || '۲۰ ثانیه',
      duration: Number(customDuration) || 3,
      difficulty: customDifficulty,
      image: customImage || undefined,
      videoUrl: customVideoUrl || undefined,
      isCustom: true
    };

    // Direct insert into Cloudflare D1 via Backend REST API
    try {
      await apiPost('/api/rehab/exercises', {
        id: newEx.id,
        module_id: 'module_1_rehab',
        scope: 'personal',
        name: newEx.name,
        category: newEx.category,
        description: newEx.description,
        purpose: newEx.purpose,
        target_muscles: newEx.targetMuscles,
        precautions: newEx.precautions || '',
        equipment: newEx.equipment || '',
        sets: newEx.sets,
        reps: newEx.reps,
        rest_time: newEx.rest,
        duration_minutes: newEx.duration,
        difficulty: newEx.difficulty,
        image_url: newEx.image || null,
        video_url: newEx.videoUrl || null,
        is_custom: 1
      });
    } catch (err) {
      console.warn('API error creating custom exercise in D1:', err);
    }

    const updated = [...rehabExercises, newEx];
    saveRehabData(updated, rehabInbox);

    // Register video/image files in cloud storage folder
    if (newEx.videoUrl) {
      saveFileRecordToCloud({
        id: `file_video_${newEx.id}`,
        name: `ویدیوی ${newEx.name}`,
        type: 'video',
        section: 'rehab',
        moduleTitle: newEx.name,
        fileUrl: newEx.videoUrl,
        uploadDate: new Date().toISOString(),
        folderPath: '/storage/rehab/videos/'
      });
    }
    if (newEx.image) {
      saveFileRecordToCloud({
        id: `file_img_${newEx.id}`,
        name: `تصویر ${newEx.name}`,
        type: 'image',
        section: 'rehab',
        moduleTitle: newEx.name,
        fileUrl: newEx.image,
        uploadDate: new Date().toISOString(),
        folderPath: '/storage/rehab/images/'
      });
    }

    // Reset Form
    setCustomName('');
    setCustomDescription('');
    setCustomPurpose('');
    setCustomTargetMuscles('');
    setCustomSets(3);
    setCustomReps('۱۰ تکرار');
    setCustomRest('۲۰ ثانیه');
    setCustomDuration(3);
    setCustomDifficulty('Beginner');
    setCustomImage(null);
    setCustomVideoUrl(null);
    setShowAddCustomForm(false);
  };

  const handleDeleteExercise = async (exerciseId: string) => {
    let confirmed = true;
    try {
      if (typeof window !== 'undefined' && window.confirm) {
        confirmed = window.confirm(t('rehabConfirmDelete', 'آیا مطمئن هستید که می‌خواهید این تمرین را از لیست خود حذف کنید؟'));
      }
    } catch {
      confirmed = true;
    }
    if (confirmed) {
      // Call D1 delete API
      try {
        await apiDelete(`/api/rehab/exercises/${exerciseId}`);
      } catch (err) {
        console.warn('API delete error:', err);
      }

      const updatedExercises = rehabExercises.filter(ex => ex.id !== exerciseId);
      
      // Also clean completed record log for safety
      let updatedCompletedIds = record.rehabCompletedIds.filter(id => id !== exerciseId);
      if (updatedCompletedIds.length !== record.rehabCompletedIds.length) {
        onUpdateRecord({
          ...record,
          rehabCompletedIds: updatedCompletedIds
        });
      }

      saveRehabData(updatedExercises, rehabInbox);
    }
  };

  const handleAcceptAiExercise = async (inboxId: string, exercise: Exercise) => {
    const updatedInbox = rehabInbox.map(item => 
      item.id === inboxId ? { ...item, status: 'accepted' as const } : item
    ).filter(item => item.id !== inboxId); // clean out inbox after accept
    const updatedExercises = [...rehabExercises, { ...exercise, isCustom: true }];
    
    try {
      await apiPost('/api/rehab/exercises', {
        id: exercise.id,
        module_id: 'module_1_rehab',
        name: exercise.name,
        category: exercise.category,
        description: exercise.description,
        purpose: exercise.purpose,
        target_muscles: exercise.targetMuscles,
        sets: exercise.sets,
        reps: exercise.reps,
        rest_time: exercise.rest,
        duration_minutes: exercise.duration,
        difficulty: exercise.difficulty,
        image_url: exercise.image || null,
        video_url: exercise.videoUrl || null,
        is_custom: 1
      });
    } catch (err) {
      console.warn('API save error for inbox exercise:', err);
    }

    saveRehabData(updatedExercises, updatedInbox);
  };

  const handleDeclineAiExercise = (inboxId: string) => {
    const updatedInbox = rehabInbox.filter(item => item.id !== inboxId);
    saveRehabData(rehabExercises, updatedInbox);
  };

  // UI Accordion details state
  const [activeExerciseId, setActiveExerciseId] = useState<string | null>(null);
  const [showSharedFilesFolder, setShowSharedFilesFolder] = useState(false);
  
  // Custom standalone timer state
  const [timerCount, setTimerCount] = useState<number | null>(null);
  const [timerRunning, setTimerRunning] = useState(false);

  // Active Guided Session Player State
  const [isGuidedMode, setIsGuidedMode] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [sessionStatus, setSessionStatus] = useState<'prepare' | 'exercise' | 'rest' | 'complete'>('prepare');
  const [secondsRemaining, setSecondsRemaining] = useState(8);
  const [isPaused, setIsPaused] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);

  const countdownIntervalRef = useRef<any>(null);

  // Standalone Timer Tick Handler
  useEffect(() => {
    let interval: any = null;
    if (timerRunning && timerCount !== null && timerCount > 0) {
      interval = setInterval(() => {
        setTimerCount((prev) => (prev !== null ? prev - 1 : null));
      }, 1000);
    } else if (timerCount === 0) {
      setTimerRunning(false);
      setTimerCount(null);
      playBeep(660, 0.25);
    }
    return () => clearInterval(interval);
  }, [timerRunning, timerCount]);

  // Guided Workout Player Countdown Master Effect
  useEffect(() => {
    if (isGuidedMode && !isPaused && sessionStatus !== 'complete') {
      countdownIntervalRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            handleGuidedStateTransition();
            return 0;
          }
          // Warn 3 seconds before ending with a soft tick
          if (prev <= 4 && prev > 1) {
            playBeep(440, 0.05);
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    }

    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [isGuidedMode, isPaused, sessionStatus, currentIdx]);

  // Trigger TTS voice notifications on state changes
  useEffect(() => {
    if (!isGuidedMode) return;

    const rawExercise = rehabExercises[currentIdx];
    if (!rawExercise) return;
    const currentExercise = rawExercise;

    const phrases = getGuidedWorkoutPhrases(lang);

    if (sessionStatus === 'prepare') {
      speakPhrase(phrases.prepare(currentExercise.name));
    } else if (sessionStatus === 'exercise') {
      speakPhrase(phrases.exercise());
    } else if (sessionStatus === 'rest') {
      const nextRawEx = rehabExercises[currentIdx + 1];
      if (nextRawEx) {
        const nextEx = nextRawEx;
        speakPhrase(phrases.restNext(nextEx.name));
      } else {
        speakPhrase(phrases.restEnd());
      }
    } else if (sessionStatus === 'complete') {
      speakPhrase(phrases.complete());
    }
  }, [sessionStatus, currentIdx, isGuidedMode, rehabExercises, lang]);

  // Voice Speech Synthesis Helper
  const speakPhrase = (text: string) => {
    if (!voiceEnabled) return;
    try {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel(); // Stop current speech
        const utterance = new SpeechSynthesisUtterance(text);
        const phrases = getGuidedWorkoutPhrases(lang);
        utterance.lang = phrases.voiceLang;
        utterance.rate = 1.0;
        window.speechSynthesis.speak(utterance);
      }
    } catch (err) {
      console.warn("Speech synthesis error:", err);
    }
    // Backup synthetic chime
    playBeep(520, 0.15);
  };

  // Browser Synthetic Beeps Generator using Web Audio API (highly reliable on iOS)
  const playBeep = (frequency = 550, duration = 0.1) => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      osc.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0.06, audioCtx.currentTime);
      
      osc.start();
      osc.stop(audioCtx.currentTime + duration);
    } catch (err) {
      console.warn("AudioContext failed:", err);
    }
  };

  // State transitions inside active guided workout
  const handleGuidedStateTransition = () => {
    const currentEx = rehabExercises[currentIdx];

    if (sessionStatus === 'prepare') {
      // Transition to active exercise (30 seconds)
      setSessionStatus('exercise');
      setSecondsRemaining(30);
    } else if (sessionStatus === 'exercise') {
      // Auto-complete the current exercise in background
      if (currentEx) {
        markExerciseCompletedInRecord(currentEx.id, true);
      }

      // Check if there's a next exercise
      if (currentIdx < rehabExercises.length - 1) {
        setSessionStatus('rest');
        setSecondsRemaining(15); // 15s rest
      } else {
        setSessionStatus('complete');
        setSecondsRemaining(0);
      }
    } else if (sessionStatus === 'rest') {
      // Transition to preparing next exercise
      const nextIndex = currentIdx + 1;
      setCurrentIdx(nextIndex);
      setSessionStatus('prepare');
      setSecondsRemaining(8);
    }
  };

  // Mark/save specific exercises as done in user storage
  const markExerciseCompletedInRecord = (id: string, completed: boolean) => {
    let updatedIds = [...record.rehabCompletedIds];
    if (completed) {
      if (!updatedIds.includes(id)) {
        updatedIds.push(id);
      }
    } else {
      updatedIds = updatedIds.filter(item => item !== id);
    }
    onUpdateRecord({
      ...record,
      rehabCompletedIds: updatedIds
    });
  };

  const toggleExerciseCompletion = (exerciseId: string) => {
    const isCompleted = record.rehabCompletedIds.includes(exerciseId);
    markExerciseCompletedInRecord(exerciseId, !isCompleted);
    if (!isCompleted) {
      // Auto trigger standalone rest timer
      startRestTimer(20);
    }
  };

  const startRestTimer = (seconds: number) => {
    setTimerCount(seconds);
    setTimerRunning(true);
  };

  const stopRestTimer = () => {
    setTimerRunning(false);
    setTimerCount(null);
  };

  // Guided Workout Controls
  const startGuidedSession = () => {
    if (!rehabExercises || rehabExercises.length === 0) {
      alert(t('rehabNoExercisesAlert', 'ابتدا باید حداقل یک تمرین به این ماژول اضافه یا ایمپورت کنید.'));
      return;
    }
    setIsGuidedMode(true);
    setCurrentIdx(0);
    setSessionStatus('prepare');
    setSecondsRemaining(8);
    setIsPaused(false);
  };

  const pauseGuidedSession = () => {
    setIsPaused(!isPaused);
  };

  const stopGuidedSession = () => {
    setIsGuidedMode(false);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  };

  const skipCurrentRest = () => {
    if (sessionStatus === 'rest' || sessionStatus === 'prepare') {
      handleGuidedStateTransition();
    } else if (sessionStatus === 'exercise') {
      // skip exercise, count as completed
      handleGuidedStateTransition();
    }
  };

  const previousExercise = () => {
    if (currentIdx > 0) {
      setCurrentIdx(prev => prev - 1);
      setSessionStatus('prepare');
      setSecondsRemaining(8);
    }
  };

  // Metrics calculating
  const totalExercises = rehabExercises.length;
  const completedCount = record.rehabCompletedIds.length;
  const completionPercent = totalExercises > 0 ? Math.round((completedCount / totalExercises) * 100) : 0;
  
  const totalCompletedTime = rehabExercises
    .filter(ex => record.rehabCompletedIds.includes(ex.id))
    .reduce((acc, curr) => acc + curr.duration, 0);

  // Decompression Illustration SVGs
  const renderMinimalIllustration = (exerciseId: string) => {
    const strokeCol = "stroke-emerald-500 dark:stroke-emerald-400";
    const bodyCol = "stroke-indigo-500 dark:stroke-indigo-400";
    
    switch(exerciseId) {
      case 'rehab_single_leg_bridge_left':
        return (
          <svg viewBox="0 0 120 60" className="w-full h-24" fill="none" strokeWidth="2">
            <line x1="10" y1="50" x2="110" y2="50" className="stroke-gray-300 dark:stroke-gray-700" strokeDasharray="3 3" />
            <circle cx="22" cy="44" r="4" className={bodyCol} />
            {/* Left foot flat on ground, right foot straight out */}
            <path d="M 26 44 L 62 26" className={strokeCol} strokeWidth="3" strokeLinecap="round" />
            {/* Supporting left leg bent */}
            <path d="M 62 26 L 82 48 M 82 48 L 90 48" className={bodyCol} strokeWidth="2.5" />
            {/* Lifted right leg straight out in the air */}
            <path d="M 62 26 L 95 20" className="stroke-pink-500" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      case 'rehab_single_leg_bridge_right':
        return (
          <svg viewBox="0 0 120 60" className="w-full h-24" fill="none" strokeWidth="2">
            <line x1="10" y1="50" x2="110" y2="50" className="stroke-gray-300 dark:stroke-gray-700" strokeDasharray="3 3" />
            <circle cx="22" cy="44" r="4" className={bodyCol} />
            {/* Right foot flat, left leg straight out in the air */}
            <path d="M 26 44 L 62 26" className={strokeCol} strokeWidth="3" strokeLinecap="round" />
            {/* Supporting right leg bent */}
            <path d="M 62 26 L 82 48 M 82 48 L 90 48" className={bodyCol} strokeWidth="2.5" />
            {/* Lifted left leg straight out in the air */}
            <path d="M 62 26 L 95 20" className="stroke-pink-500" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      case 'rehab_side_plank_hip_lift_left':
        return (
          <svg viewBox="0 0 120 60" className="w-full h-24" fill="none" strokeWidth="2">
            <line x1="10" y1="50" x2="110" y2="50" className="stroke-gray-300 dark:stroke-gray-700" strokeDasharray="3 3" />
            {/* Left forearm on ground, shoulder to hips elevated */}
            <line x1="30" y1="48" x2="45" y2="35" className={bodyCol} strokeWidth="3" strokeLinecap="round" />
            <circle cx="30" cy="22" r="4" className={bodyCol} />
            {/* Body line from head-neck to feet */}
            <path d="M 34 24 L 95 42" className={strokeCol} strokeWidth="3.5" strokeLinecap="round" />
            {/* Hip lifting arrow */}
            <path d="M 65 38 L 65 24 M 65 24 L 61 28 M 65 24 L 69 28" className="stroke-pink-500" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        );
      case 'rehab_side_plank_hip_lift_right':
        return (
          <svg viewBox="0 0 120 60" className="w-full h-24" fill="none" strokeWidth="2">
            <line x1="10" y1="50" x2="110" y2="50" className="stroke-gray-300 dark:stroke-gray-700" strokeDasharray="3 3" />
            {/* Right forearm on ground, shoulder to hips elevated */}
            <line x1="30" y1="48" x2="45" y2="35" className={bodyCol} strokeWidth="3" strokeLinecap="round" />
            <circle cx="30" cy="22" r="4" className={bodyCol} />
            {/* Body line */}
            <path d="M 34 24 L 95 42" className={strokeCol} strokeWidth="3.5" strokeLinecap="round" />
            {/* Hip lifting arrow */}
            <path d="M 65 38 L 65 24 M 65 24 L 61 28 M 65 24 L 69 28" className="stroke-pink-500" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        );
      case 'rehab_bird_dog':
        return (
          <svg viewBox="0 0 120 60" className="w-full h-24" fill="none" strokeWidth="2">
            <line x1="10" y1="50" x2="110" y2="50" className="stroke-gray-300 dark:stroke-gray-700" strokeDasharray="3 3" />
            <circle cx="45" cy="25" r="4" className={bodyCol} />
            {/* Flat back */}
            <path d="M 49 27 L 85 27" className={strokeCol} strokeWidth="3" strokeLinecap="round" />
            {/* Left arm extended straight out */}
            <path d="M 45 27 L 20 27" className="stroke-pink-500" strokeWidth="2" strokeLinecap="round" />
            {/* Right leg extended straight back */}
            <path d="M 85 27 L 110 27" className="stroke-pink-500" strokeWidth="2" strokeLinecap="round" />
            {/* Supporting left knee and right arm to ground */}
            <path d="M 55 27 L 55 50" className={bodyCol} strokeWidth="2" />
            <path d="M 75 27 L 75 50" className={bodyCol} strokeWidth="2" />
          </svg>
        );
      case 'rehab_plank':
        return (
          <svg viewBox="0 0 120 60" className="w-full h-24" fill="none" strokeWidth="2">
            <line x1="10" y1="50" x2="110" y2="50" className="stroke-gray-300 dark:stroke-gray-700" strokeDasharray="3 3" />
            <circle cx="25" cy="24" r="4" className={bodyCol} />
            {/* Flat straight body plank */}
            <path d="M 29 26 L 105 38" className={strokeCol} strokeWidth="3.5" strokeLinecap="round" />
            {/* Forearm supporting on ground */}
            <path d="M 33 28 L 38 48 L 48 48" className={bodyCol} strokeWidth="2.5" strokeLinecap="round" />
            {/* Feet supporting on ground */}
            <path d="M 102 37 L 105 48" className={bodyCol} strokeWidth="2" />
          </svg>
        );
      case 'rehab_upper_circle_crunch':
        return (
          <svg viewBox="0 0 120 60" className="w-full h-24" fill="none" strokeWidth="2">
            <line x1="10" y1="50" x2="110" y2="50" className="stroke-gray-300 dark:stroke-gray-700" strokeDasharray="3 3" />
            <circle cx="45" cy="30" r="4" className={bodyCol} />
            {/* Neck and slightly lifted upper chest */}
            <path d="M 48 32 Q 65 18 95 45" className={strokeCol} strokeWidth="3" strokeLinecap="round" />
            <path d="M 95 45 L 110 45" className={bodyCol} strokeWidth="2" />
            {/* Circular movement cue */}
            <path d="M 40 18 C 45 10, 58 10, 60 18" className="stroke-pink-500" strokeWidth="1.5" strokeDasharray="2 2" />
            <path d="M 60 18 L 56 16 M 60 18 L 58 21" className="stroke-pink-500" strokeWidth="1.5" />
          </svg>
        );
      case 'rehab_reverse_crunch':
        return (
          <svg viewBox="0 0 120 60" className="w-full h-24" fill="none" strokeWidth="2">
            <line x1="10" y1="50" x2="110" y2="50" className="stroke-gray-300 dark:stroke-gray-700" strokeDasharray="3 3" />
            {/* Head and back flat */}
            <circle cx="20" cy="45" r="4" className={bodyCol} />
            <path d="M 24 47 L 70 47" className={strokeCol} strokeWidth="3" />
            {/* Legs bent in air 90 degrees */}
            <path d="M 70 47 Q 85 22 105 22" className="stroke-pink-500" strokeWidth="2.5" strokeLinecap="round" />
            {/* Direction arrow indicating lifting hips up/back */}
            <path d="M 66 40 Q 60 30 65 24 M 65 24 L 61 27 M 65 24 L 68 28" className="stroke-pink-500" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        );
      default:
        return (
          <div className="w-full h-24 bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-xs text-gray-400">
            {t('rehabGraphicPlaceholder', '[نمای شماتیک فیزیوتراپی]')}
          </div>
        );
    }
  };

  return (
    <div id="rehab-module-view" className="space-y-6 animate-fadeIn">
      
      {/* 1. Header & Quick Actions */}
      <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-2xl font-semibold font-display text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-emerald-500 animate-pulse" />
            {t('rehabTitle', 'فیزیوتراپی، تمرینات اصلاحی و تسکین درد')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('rehabSubtitle', 'برنامه تمرینی ملایم برای آزادسازی پسواس، افزایش دامنه حرکتی و تقویت عضلات پایدارکننده؛ استخراج شده از فیزیوتراپی پیشرفته.')}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 self-start md:self-center">
          <button
            onClick={() => setShowSharedFilesFolder(!showSharedFilesFolder)}
            className="px-4 py-2.5 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer"
          >
            <Folder className="w-4 h-4 text-emerald-500" />
            {showSharedFilesFolder ? t('rehabFolderBtnClose', 'بستن پوشه فایل‌ها') : t('rehabFolderBtnOpen', 'پوشه فایل‌ها و اسناد فیزیوتراپی')}
          </button>

          {!isGuidedMode && (
            <button 
              onClick={startGuidedSession}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-md hover:shadow-lg active:scale-95 flex items-center gap-2"
            >
              <Play className="w-4 h-4 fill-current rotate-180" />
              {t('rehabStartGuided', 'شروع جلسه تمرینی هدایت‌شده با صدا')}
            </button>
          )}
        </div>
      </div>

      {/* Shared Cloud File Folder View */}
      {showSharedFilesFolder && (
        <SharedFileManager initialSectionFilter="rehab" onClose={() => setShowSharedFilesFolder(false)} />
      )}

      {/* 2. ACTIVE GUIDED SESSION PLAYER WIDGET (when active) */}
      {isGuidedMode && (
        <div className="premium-card p-6 border-2 border-emerald-500 bg-gradient-to-br from-emerald-500/10 to-indigo-500/5 dark:from-gray-900 dark:to-gray-950 space-y-6 animate-slideIn">
          
          {/* Player status header */}
          <div className="flex justify-between items-center border-b pb-3.5 border-emerald-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <span className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
              </span>
              <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400">{t('rehabVoiceActive', 'دستیار صوتی فعال است')}</span>
            </div>

            <div className="flex items-center gap-3">
              {/* Voice toggle */}
              <button 
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50"
                title={voiceEnabled ? t('muteAudio', 'قطع صدا') : t('unmuteAudio', 'وصل صدا')}
              >
                {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-red-500" />}
              </button>

              <button 
                onClick={stopGuidedSession}
                className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400 rounded-lg text-xs font-bold transition-colors flex items-center gap-1"
              >
                <Square className="w-3.5 h-3.5 fill-current" />
                {t('fatExitSession', 'خروج از جلسه')}
              </button>
            </div>
          </div>

          {sessionStatus === 'complete' ? (
            // Complete state
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto">
                <CheckCircle2 className="w-10 h-10 fill-current" />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('rehabGuidedCompleteTitle', 'آفرین! تمرین امروز تکمیل شد')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('rehabGuidedCompleteDesc', 'مجموعه تمرینات ریکاوری کمر با موفقیت در جدول پیشرفت ذخیره شدند.')}</p>
              </div>
              <button 
                onClick={stopGuidedSession}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all"
              >
                {t('rehabGuidedSaveReturn', 'تایید و بازگشت به لیست')}
              </button>
            </div>
          ) : (
            // Active exercise guide block
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              
              {/* Left Column: Visual & Countdown (5 cols) */}
              <div className="lg:col-span-5 flex flex-col items-center justify-center text-center space-y-4">
                
                {/* Visual Status Indicator Circular timer wrapper */}
                <div className="relative w-36 h-36 flex items-center justify-center bg-white dark:bg-gray-800 rounded-full border-4 border-emerald-500/20 shadow-md">
                  <div className="text-center space-y-1">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">
                      {sessionStatus === 'prepare' ? t('rehabStatusPrepare', 'آماده‌باش') :
                       sessionStatus === 'exercise' ? t('rehabStatusRunning', 'در حال اجرا') : t('rehabStatusRest', 'استراحت و ریکاوری')}
                    </span>
                    <span className="text-4xl font-bold font-mono text-gray-900 dark:text-white">
                      {secondsRemaining}
                    </span>
                    <span className="text-[10px] text-gray-400 block">{t('seconds', 'ثانیه')}</span>
                  </div>
                </div>

                {/* Progress dot indicator */}
                <div className="space-y-1">
                  <div className="text-xs font-semibold text-gray-400">
                    {t('fatExerciseXofY', 'حرکت {{current}} از {{total}}', { current: formatNumberByLang(currentIdx + 1, lang), total: formatNumberByLang(rehabExercises.length, lang) })}
                  </div>
                  <div className="flex gap-1.5">
                    {rehabExercises.map((_, i) => (
                      <div 
                        key={i} 
                        className={`h-1.5 w-3 rounded-full transition-all duration-300 ${
                          i === currentIdx ? 'bg-emerald-500 w-6' : 
                          i < currentIdx ? 'bg-emerald-300' : 'bg-gray-200 dark:bg-gray-700'
                        }`} 
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: Descriptions & Silhouette (7 cols) */}
              <div className="lg:col-span-7 space-y-4 text-right">
                {(() => {
                  const rawEx = rehabExercises[currentIdx];
                  if (!rawEx) return null;
                  const currentExercise = rawEx;
                  return (
                    <>
                      <div className="space-y-1">
                        <span className="text-[11px] font-bold bg-indigo-500/10 text-indigo-600 dark:bg-indigo-500/5 dark:text-indigo-400 px-2.5 py-1 rounded-full uppercase tracking-wider inline-block">
                          {rawEx.difficulty === 'Beginner' ? t('rehabLevelSafe', 'سطح مبتنی ایمن') : t('rehabLevelMid', 'متوسط')}
                        </span>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white font-display">
                          {currentExercise.name}
                        </h3>
                      </div>

                      {/* SHOW THE ABSOLUTE FULL DESCRIPTION HERE - CLEAN & CONCISE */}
                      <div className="p-3.5 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 leading-relaxed shadow-sm">
                        <strong className="text-gray-800 dark:text-white block mb-1">{t('rehabFullInstructions', 'آموزش گام‌به‌گام انجام حرکت:')}</strong>
                        {currentExercise.description}
                      </div>

                      <div className="grid grid-cols-2 gap-2.5 text-[11px]">
                        <div className="p-2 bg-white/50 dark:bg-gray-900/50 rounded-lg">
                          <span className="text-gray-400 block font-medium">{t('rehabTargetPurpose', 'هدف کلیدی تمرین:')}</span>
                          <span className="font-semibold text-gray-800 dark:text-gray-200">{currentExercise.purpose}</span>
                        </div>
                        <div className="p-2 bg-white/50 dark:bg-gray-900/50 rounded-lg">
                          <span className="text-gray-400 block font-medium">{t('rehabTargetMuscles', 'عضلات تثبیت‌کننده:')}</span>
                          <span className="font-semibold text-gray-800 dark:text-gray-200">{currentExercise.targetMuscles}</span>
                        </div>
                      </div>

                      {/* Silhouette guide visual or Media viewer */}
                      <div className="bg-white/80 dark:bg-gray-900/40 rounded-xl p-2 border border-gray-100 dark:border-gray-800">
                        {rawEx.videoUrl || rawEx.image ? (
                          <ExerciseMediaViewer exercise={rawEx} />
                        ) : (
                          renderMinimalIllustration(rawEx.id)
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* Playback Controls Row */}
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <button 
                    onClick={pauseGuidedSession}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-1.5"
                  >
                    {isPaused ? <Play className="w-3.5 h-3.5 fill-current rotate-180" /> : <Pause className="w-3.5 h-3.5 fill-current" />}
                    {isPaused ? t('fatResume', 'ادامه') : t('fatPause', 'توقف موقت')}
                  </button>

                  <button 
                    onClick={skipCurrentRest}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-semibold transition-all flex items-center gap-1"
                  >
                    <SkipForward className="w-3.5 h-3.5" />
                    {sessionStatus === 'exercise' ? t('fatNextEx', 'ثبت و حرکت بعدی') : t('fatSkipRest', 'رد کردن استراحت')}
                  </button>

                  {currentIdx > 0 && (
                    <button 
                      onClick={previousExercise}
                      className="px-3 py-2 bg-gray-50 hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-800 text-gray-500 rounded-xl text-xs font-semibold transition-all"
                    >
                      {t('fatPrevEx', 'حرکت قبلی')}
                    </button>
                  )}
                </div>

              </div>

            </div>
          )}

        </div>
      )}

      {/* 3. Stats and Dynamic Rest Timers Panel (when not in guided mode) */}
      {!isGuidedMode && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="premium-card p-5 dark:bg-gray-800 flex flex-col justify-between">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">{t('rehabStatsTitleCompleted', 'تمرینات تکمیل‌شده کمردرد')}</span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-bold font-display text-gray-900 dark:text-white">{completedCount}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('rehabStatsTotalDaily', '/ {{total}} تمرین روزانه', { total: totalExercises })}</span>
              </div>
              <div className="w-full bg-gray-100 dark:bg-gray-700 h-2 rounded-full mt-4 overflow-hidden">
                <div 
                  className="bg-emerald-500 h-full rounded-full transition-all duration-500" 
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
            </div>

            <div className="premium-card p-5 dark:bg-gray-800 flex flex-col justify-between">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">{t('rehabStatsTimeTitle', 'مدت‌زمان ریکاوری مفید')}</span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-bold font-display text-gray-900 dark:text-white">{totalCompletedTime}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('rehabStatsTimeActive', 'دقیقه تمرین فعال')}</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                {t('rehabStatsTimeTip', 'استمرار در تمرینات کلید اصلی بهبودی است!')}
              </p>
            </div>

            {/* Standalone recovery timer */}
            <div className="premium-card p-5 dark:bg-gray-800 border-indigo-100 dark:border-indigo-950/40 bg-indigo-50/10 flex flex-col justify-between">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-indigo-500 uppercase tracking-wider">{t('rehabTimerTitle', 'تایمر استراحت آزاد')}</span>
                <Timer className="w-4 h-4 text-indigo-400" />
              </div>
              {timerCount !== null ? (
                <div className="mt-2 flex items-center justify-between">
                  <div>
                    <span className="text-3xl font-bold font-mono text-indigo-600 dark:text-indigo-400">{timerCount}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">{t('seconds', 'ثانیه')}</span>
                  </div>
                  <button 
                    onClick={() => stopRestTimer()}
                    className="px-3 py-1.5 bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-950/40 dark:text-red-400 rounded-xl text-xs font-semibold transition-colors"
                  >
                    {t('rehabTimerSkip', 'رد کردن استراحت')}
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('rehabTimerStandby', 'آماده‌به‌کار')}</span>
                  <button 
                    onClick={() => startRestTimer(30)}
                    className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold transition-all flex items-center gap-1 shadow-sm"
                  >
                    <Play className="w-3 h-3 fill-current rotate-180" />
                    {t('rehabTimerStart30', 'شروع ۳۰ ثانیه')}
                  </button>
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-3">
                {t('rehabTimerTip', 'برای خون‌رسانی و ریکاوری بافت‌های همبند کمر، اختصاص دادن ۳۰ ثانیه استراحت بین تکرارها سودمند است.')}
              </p>
            </div>
          </div>

          {/* Safety alert */}
          <div className="bg-amber-500/5 border border-amber-500/20 p-4 rounded-xl flex gap-3 text-xs text-amber-800 dark:text-amber-200">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold block mb-0.5">{t('rehabAlertTitle', 'احتیاط در تمرینات کمر')}</span>
              {t('rehabAlertDesc', 'اگر در هر یک از این حرکات دچار افزایش درد موضعی یا دردهای تیرکشنده به سمت باسن یا پا شدید، بلافاصله تمرین را متوقف کنید. حرکات دکومپرشن و کشش پسواس باید همیشه بدون درد و در کمال آرامش عضلانی انجام پذیرند.')}
            </div>
          </div>

          {/* AI Rehabilitation Assistant Section */}
          <div className="space-y-4 my-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl">
                <Sparkles className="w-5 h-5 animate-pulse" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white font-display">{t('rehabAiTitle', 'تولید هوشمند تمرین توانبخشی (هوش مصنوعی)')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('rehabAiSubtitle', 'تنظیم تمرینات تخصصی فیزیوتراپی بر اساس ارزیابی کامل وضعیت جسمانی و درد شما')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Patient Intake Assessment Form */}
              <div className="premium-card p-4 dark:bg-gray-800 border-indigo-100 dark:border-indigo-950/40 bg-indigo-50/10 space-y-3">
                <div className="flex items-center gap-1.5 border-b pb-2 border-indigo-100 dark:border-indigo-900/40">
                  <Sliders className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <h4 className="text-xs font-bold text-gray-900 dark:text-white">{t('rehabAiFormTitle', 'ارزیابی وضعیت بدنی و درد')}</h4>
                </div>

                <div className="space-y-2 text-xs">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      {t('rehabAiFormLoc', 'محل درد اصلی:')}
                    </label>
                    <input
                      type="text"
                      value={painLocation}
                      onChange={(e) => setPainLocation(e.target.value)}
                      placeholder={t('rehabAiFormLocPh', 'مثال: کمر، گردن، زانو، شانه')}
                      className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      {t('rehabAiFormLevel', 'شدت درد ({{level}} از ۱۰):', { level: painLevel })}
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={painLevel}
                      onChange={(e) => setPainLevel(Number(e.target.value))}
                      className="w-full accent-indigo-600"
                    />
                    <div className="flex justify-between text-[9px] text-gray-400">
                      <span>{t('rehabAiFormLevelMin', 'خیلی خفیف (۱)')}</span>
                      <span>{t('rehabAiFormLevelMid', 'متوسط (۵)')}</span>
                      <span>{t('rehabAiFormLevelMax', 'شدید (۱۰)')}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      {t('rehabAiFormLimit', 'محدودیت‌های حرکتی:')}
                    </label>
                    <input
                      type="text"
                      value={limitations}
                      onChange={(e) => setLimitations(e.target.value)}
                      placeholder={t('rehabAiFormLimitPh', 'مثال: عدم امکان خم شدن، درد هنگام نشستن طولانی')}
                      className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      {t('rehabAiFormEq', 'تجهیزات در دسترس:')}
                    </label>
                    <input
                      type="text"
                      value={equipment}
                      onChange={(e) => setEquipment(e.target.value)}
                      placeholder={t('rehabAiFormEqPh', 'مثال: مت یوگا، کش پیلاتس، صندلی')}
                      className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      {t('rehabAiFormExp', 'سابقه ورزشی:')}
                    </label>
                    <select
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-xs"
                    >
                      <option value="Beginner">{t('rehabAiFormExpB', 'مبتدی (بدون سابقه ورزشی)')}</option>
                      <option value="Intermediate">{t('rehabAiFormExpM', 'متوسط (تمرینات پراکنده)')}</option>
                      <option value="Advanced">{t('rehabAiFormExpA', 'حرفه‌ای (ورزشکار منظم)')}</option>
                    </select>
                  </div>
                </div>

                <div className="p-2.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 rounded-lg text-[10px] text-amber-800 dark:text-amber-300 flex items-start gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
                  <span>{t('rehabAiAlert', 'توصیه‌ها توسط هوش مصنوعی آنلاین تولید می‌شوند. در صورت بروز درد شدید غیرمعمول تمرین را متوقف کنید.')}</span>
                </div>
              </div>

              {/* Chat Interface with AI Worker */}
              <div className="lg:col-span-2">
                <AiChatBox
                  feature="rehabilitation"
                  title={t('rehabChatTitle', 'گفتگوی هوشمند توانبخشی و طراحی تمرین')}
                  subtitle={t('rehabChatSub', 'پاسخ مستقیم و هوشمند بر اساس ارزیابی بدنی شما')}
                  placeholder={t('rehabChatPh', 'شرح کامل درد یا سوال خود درباره حرکات را بنویسید...')}
                  initialContext={`${t('rehabIntakeUserInfo', 'اطلاعات ارزیابی بدنی کاربر:')}\n- ${t('rehabAiFormLoc', 'محل درد:')} ${painLocation}\n- ${t('rehabAiFormLevel', 'میزان درد:')} ${painLevel} / 10\n- ${t('rehabAiFormLimit', 'محدودیت حرکتی:')} ${limitations}\n- ${t('rehabAiFormEq', 'تجهیزات:')} ${equipment}\n- ${t('rehabAiFormExp', 'سابقه ورزشی:')} ${experience}\n\n${t('rehabPromptAiInstructions', 'دستورالعمل مهم: پاسخ را بسیار کوتاه (حداکثر ۲ تا ۳ جمله بدون هیچگونه علامت Markdown مانند ## یا ** یا جدول) بنویسید تا مستقیماً به عنوان دستورالعمل تمرین ثبت شود.')}`}
                  onApplyResult={handleApplyParsedRehabExercise}
                  applyButtonText={t('rehabChatApply', 'افزودن این تمرین به لیست برنامه‌ها')}
                />
              </div>
            </div>
          </div>

          {/* Sub-Tabs: Multi-Day Programs vs Exercise Library */}
          <div className="flex border-b border-gray-200 dark:border-gray-800 gap-6 my-6">
            <button
              onClick={() => setRehabSubTab('programs')}
              className={`pb-3 text-sm font-bold border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                rehabSubTab === 'programs'
                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <Layers className="w-4 h-4" />
              <span>{t('multiDayProgramsTab', 'برنامه‌های تمرینی چندروزه')}</span>
              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 rounded-full text-xs">
                {exercisePrograms.length}
              </span>
            </button>

            <button
              onClick={() => setRehabSubTab('library')}
              className={`pb-3 text-sm font-bold border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                rehabSubTab === 'library'
                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <Dumbbell className="w-4 h-4" />
              <span>{t('exerciseLibraryTab', 'بانک حرکات تکی (Library)')}</span>
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-xs">
                {rehabExercises.length}
              </span>
            </button>
          </div>

          {rehabSubTab === 'programs' ? (
            <ProgramListManager
              programs={exercisePrograms}
              activeProgramId={activeProgramId}
              availableExercises={rehabExercises}
              record={record}
              onUpdateRecord={onUpdateRecord}
              onProgramsChanged={(updated) => {
                setExercisePrograms(updated);
                const appState = loadAppState();
                appState.settings.exercisePrograms = updated;
                saveAppState(appState, false);
              }}
              onSelectActiveProgram={(progId) => {
                setActiveProgramId(progId);
                const appState = loadAppState();
                appState.settings.activeProgramId = progId;
                saveAppState(appState, false);
              }}
              onStartGuidedWorkout={(day, prog) => {
                startGuidedSessionForDay(day, prog);
              }}
              onScheduleDay={(day, prog) => {
                setSchedulingDayInfo({ day, program: prog });
              }}
            />
          ) : (
            <>
            {/* Manual Custom Exercise Adder & Cloud Bulk Import */}
            <div className="premium-card p-5 dark:bg-gray-800 border-gray-100 dark:border-gray-800 flex flex-col justify-between">
              <div>
                <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 rounded-lg">
                      <Plus className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white font-display">{t('rehabCustomAddTitle', 'افزودن تمرین فیزیوتراپی سفارشی')}</h3>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">{t('rehabCustomAddSub', 'یک حرکت دلخواه یا توصیه شده پزشک را به برنامه شخصی خود اضافه کنید')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowBulkImportModal(true)}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
                      title={t('bulkImportOpenBtn', 'درون‌ریزی دسته‌جمعی فعالیت‌ها (JSON Import)')}
                    >
                      <UploadCloud className="w-3.5 h-3.5" />
                      <span>{t('bulkImportOpenBtn', 'درون‌ریزی دسته‌جمعی فعالیت‌ها (JSON Import)')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => refreshExercisesFromCloud()}
                      disabled={isSyncingCloud}
                      className="p-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-xl text-xs transition-all cursor-pointer"
                      title={t('syncCloudDbTooltip', 'همگام‌سازی با پایگاه داده Cloudflare D1')}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${isSyncingCloud ? 'animate-spin text-purple-600' : ''}`} />
                    </button>
                    <button
                      onClick={() => setShowAddCustomForm(!showAddCustomForm)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                        showAddCustomForm 
                          ? 'bg-red-50 text-red-600 dark:bg-red-950/20 dark:text-red-400' 
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {showAddCustomForm ? t('rehabCustomClose', 'بستن فرم') : t('rehabCustomOpen', 'افزودن تکی')}
                    </button>
                  </div>
                </div>

                {showAddCustomForm ? (
                  <form onSubmit={handleAddCustomExercise} className="space-y-2 mt-2 animate-fadeIn">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <label className="block text-[10px] text-gray-400 font-semibold">{t('rehabCustomName', 'نام حرکت')}</label>
                          <VoiceInputButton
                            currentValue={customName}
                            onUpdate={(val) => setCustomName(val)}
                            title={t('voiceInputNormal', 'ورودی صوتی')}
                          />
                        </div>
                        <input
                          type="text"
                          required
                          value={customName}
                          onChange={(e) => setCustomName(e.target.value)}
                          placeholder={t('rehabCustomNamePh', 'مثال: کشش گربه-شتر ملایم')}
                          className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <label className="block text-[10px] text-gray-400 font-semibold">{t('rehabCustomTarget', 'عضلات تحت تأثیر')}</label>
                          <VoiceInputButton
                            currentValue={customTargetMuscles}
                            onUpdate={(val) => setCustomTargetMuscles(val)}
                            title={t('voiceInputNormal', 'ورودی صوتی')}
                          />
                        </div>
                        <input
                          type="text"
                          value={customTargetMuscles}
                          onChange={(e) => setCustomTargetMuscles(e.target.value)}
                          placeholder={t('rehabCustomTargetPh', 'مثال: فیله کمر، ستون فقرات')}
                          className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-0.5">
                        <label className="block text-[10px] text-gray-400 font-semibold">{t('rehabCustomDesc', 'توضیح کامل حرکت و نحوه انجام')}</label>
                        <VoiceInputButton
                          currentValue={customDescription}
                          onUpdate={(val) => setCustomDescription(val)}
                          title={t('voiceInputNormal', 'ورودی صوتی')}
                        />
                      </div>
                      <textarea
                        required
                        rows={2}
                        value={customDescription}
                        onChange={(e) => setCustomDescription(e.target.value)}
                        placeholder={t('rehabCustomDescPh', 'حرکت را دقیقاً توصیف کنید تا هنگام تمرین راهنما باشد...')}
                        className="w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5 font-semibold">{t('rehabCustomPurpose', 'هدف حرکت (چرا کمک می‌کند؟)')}</label>
                        <input
                          type="text"
                          value={customPurpose}
                          onChange={(e) => setCustomPurpose(e.target.value)}
                          placeholder={t('rehabCustomPurposePh', 'مثال: کاهش فشردگی مهره‌های L4-S1')}
                          className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5 font-semibold">{t('rehabCustomSets', 'تعداد ست')}</label>
                          <input
                            type="number"
                            min={1}
                            value={customSets}
                            onChange={(e) => setCustomSets(Number(e.target.value))}
                            className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 font-mono text-center"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5 font-semibold">{t('rehabCustomTime', 'زمان (دقیقه)')}</label>
                          <input
                            type="number"
                            min={1}
                            value={customDuration}
                            onChange={(e) => setCustomDuration(Number(e.target.value))}
                            className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 font-mono text-center"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5 font-semibold">{t('rehabCustomReps', 'تکرار / مکث')}</label>
                        <input
                          type="text"
                          value={customReps}
                          onChange={(e) => setCustomReps(e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-center"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5 font-semibold">{t('rehabCustomRest', 'مدت استراحت')}</label>
                        <input
                          type="text"
                          value={customRest}
                          onChange={(e) => setCustomRest(e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-center"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5 font-semibold">{t('rehabCustomDiff', 'درجه سختی')}</label>
                        <select
                          value={customDifficulty}
                          onChange={(e: any) => setCustomDifficulty(e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-center"
                        >
                          <option value="Beginner">{t('rehabCustomDiffB', 'مبتدی')}</option>
                          <option value="Intermediate">{t('rehabCustomDiffM', 'متوسط')}</option>
                          <option value="Advanced">{t('rehabCustomDiffA', 'پیشرفته')}</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5 font-semibold">{t('rehabCustomVideo', 'فیلم آموزشی حرکت (اختیاری)')}</label>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => handleVideoFileChange(e, setCustomVideoUrl)}
                          className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 dark:file:bg-gray-800 dark:file:text-gray-300 cursor-pointer mb-1"
                        />
                        <div className="flex items-center gap-1">
                          <Link className="w-3 h-3 text-gray-400 shrink-0" />
                          <input
                            type="text"
                            placeholder={t('rehabCustomVideoPh', 'یا لینک ویدیو (اینستاگرام، یوتیوب، آپارات، MP4)...')}
                            value={customVideoUrl && !customVideoUrl.startsWith('data:') ? customVideoUrl : ''}
                            onChange={(e) => setCustomVideoUrl(e.target.value)}
                            className="w-full text-[11px] px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                          />
                        </div>
                        {customVideoUrl && (
                          <div className="mt-1 flex items-center justify-between bg-purple-50 dark:bg-purple-950/30 p-1.5 rounded-lg text-xs text-purple-700 dark:text-purple-300">
                            <span className="flex items-center gap-1 text-[10px] font-semibold">
                              <Film className="w-3.5 h-3.5 text-purple-600" />
                              {t('rehabCustomVideoReady', 'ویدیو آماده است')}
                            </span>
                            <button
                              type="button"
                              onClick={() => setCustomVideoUrl(null)}
                              className="text-red-500 hover:text-red-700 text-[10px] font-bold"
                            >
                              {t('rehabCustomVideoDel', 'حذف ویدیو')}
                            </button>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5 font-semibold">{t('rehabCustomImage', 'تصویر آموزشی حرکت (اختیاری)')}</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 dark:file:bg-gray-800 dark:file:text-gray-300 cursor-pointer"
                        />
                        {customImage && (
                          <div className="mt-1 flex items-center gap-2">
                            <img src={customImage} alt="Custom Preview" className="w-8 h-8 object-cover rounded border border-gray-200 dark:border-gray-700 shadow-sm" />
                            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">{t('rehabCustomImageReady', 'تصویر بارگذاری شد')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full mt-2 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition-all shadow-sm flex items-center justify-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t('rehabCustomSubmit', 'ثبت و ذخیره حرکت سفارشی')}
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-xs border border-dashed border-gray-200 dark:border-gray-700 rounded-xl my-2">
                    {t('rehabCustomTip1', 'با باز کردن فرم می‌توانید هر حرکتی را به همراه جزئیات کامل ست‌ها، تکرارها، عضلات هدف و تصاویر/فیلم‌های آموزشی اختصاصی به لیست روزانه خود متصل نمایید.')}
                  </div>
                )}
              </div>

              <p className="text-[11px] text-gray-400 mt-2">
                {t('rehabCustomTip2', 'حرکاتی که به این جدول اضافه می‌کنید هم در بخش ورزش‌های روزانه هم در برنامه هدایت صوتی پویا به صورت خودکار قرار خواهند گرفت.')}
              </p>
            </div>

          {/* 4. Exercise cards (classic view) or empty state */}
          {rehabExercises.length === 0 ? (
            <div className="premium-card p-8 text-center space-y-4 border-dashed border-2 border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/30">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-950/40 rounded-2xl flex items-center justify-center mx-auto text-emerald-600 dark:text-emerald-400">
                <Activity className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-bold text-gray-800 dark:text-gray-200">
                  {t('rehabEmptyTitle', 'هنوز تمرینی در این ماژول ثبت نشده است')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                  {t('rehabEmptyDesc', 'می‌توانید با استفاده از دکمه «درون‌ریزی دسته‌جمعی فعالیت‌ها» تمرینات خود را از فایل JSON وارد کنید یا به صورت تکی تمرین جدید اضافه نمایید.')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBulkImportModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
              >
                <UploadCloud className="w-4 h-4" />
                <span>{t('bulkImportOpenBtn', 'درون‌ریزی دسته‌جمعی فعالیت‌ها (JSON Import)')}</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Movement Library Scope & Category Filter Tabs */}
              <div className="flex flex-col gap-3 p-3 bg-gray-50/80 dark:bg-gray-800/60 rounded-2xl border border-gray-200 dark:border-gray-700/80">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Category Filter */}
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{t('rehabCategoryFilterLabel', 'دسته‌بندی:')}</span>
                    <div className="flex items-center gap-1 p-1 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 text-xs">
                      <button
                        type="button"
                        onClick={() => setLibraryCategoryFilter('all')}
                        className={`px-3 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                          libraryCategoryFilter === 'all'
                            ? 'bg-purple-600 text-white shadow-xs'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {t('catFilterAll', 'همه موضوعات')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setLibraryCategoryFilter('rehab')}
                        className={`px-3 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                          libraryCategoryFilter === 'rehab'
                            ? 'bg-emerald-600 text-white shadow-xs'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {t('catFilterRehab', 'فیزیوتراپی و اصلاحی')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setLibraryCategoryFilter('fat-loss')}
                        className={`px-3 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                          libraryCategoryFilter === 'fat-loss'
                            ? 'bg-amber-600 text-white shadow-xs'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {t('catFilterFatLoss', 'چربی‌سوزی و کاردیو')}
                      </button>
                    </div>

                    {/* Scope Filter */}
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300 mr-2">{t('rehabLibraryScopeFilter', 'دامنه حرکات:')}</span>
                    <div className="flex items-center gap-1 p-1 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 text-xs">
                      <button
                        type="button"
                        onClick={() => setLibraryFilterScope('all')}
                        className={`px-3 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                          libraryFilterScope === 'all'
                            ? 'bg-purple-600 text-white shadow-xs'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {t('rehabFilterAll', 'همه')} ({formatNumberByLang(rehabExercises.length, lang)})
                      </button>
                      <button
                        type="button"
                        onClick={() => setLibraryFilterScope('global')}
                        className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                          libraryFilterScope === 'global'
                            ? 'bg-blue-600 text-white shadow-xs'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        <Globe className="w-3.5 h-3.5" />
                        <span>{t('rehabFilterGlobal', 'عمومی')} ({formatNumberByLang(rehabExercises.filter(e => !e.user_id && e.scope !== 'personal').length, lang)})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setLibraryFilterScope('personal')}
                        className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                          libraryFilterScope === 'personal'
                            ? 'bg-emerald-600 text-white shadow-xs'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        <User className="w-3.5 h-3.5" />
                        <span>{t('rehabFilterPersonal', 'شخصی')} ({formatNumberByLang(rehabExercises.filter(e => Boolean(e.user_id) || e.scope === 'personal').length, lang)})</span>
                      </button>
                    </div>

                    {/* Admin Only: Publish All Personal Exercises to Global Library */}
                    {isGlobalPublisherAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          setPublishAllResults(null);
                          setShowPublishAllModal(true);
                        }}
                        className="px-3 py-1.5 bg-linear-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 border border-amber-400 cursor-pointer"
                        title={t('publishAllToGlobalTooltip', 'انتقال و انتشار یکپارچه تمامی تمرینات شخصی واجد شرایط به بانک عمومی دیتابیس D1 (مخصوص مدیریت)')}
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-200" />
                        <Globe className="w-3.5 h-3.5" />
                        <span>{t('publishAllToGlobal', 'انتشار همه حرکات شخصی در بانک عمومی')}</span>
                      </button>
                    )}
                  </div>

                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    {libraryFilterScope === 'global' 
                      ? t('globalScopeExpl', 'حرکات عمومی مشترک بین همه کاربران. برای تغییر دلخواه، «ایجاد نسخه شخصی» را بزنید.')
                      : libraryFilterScope === 'personal'
                      ? t('personalScopeExpl', 'حرکات شخصی مختص شما که قابل ویرایش و حذف اختصاصی هستند.')
                      : t('allScopeExpl', 'بانک کامل شامل حرکات استاندارد عمومی و تمرینات اختصاصی شما.')
                    }
                  </div>
                </div>
              </div>

              {(() => {
                const filteredExercises = rehabExercises.filter(e => {
                  const isPersonal = Boolean(e.user_id) || e.scope === 'personal';
                  if (libraryFilterScope === 'global' && isPersonal) return false;
                  if (libraryFilterScope === 'personal' && !isPersonal) return false;

                  if (libraryCategoryFilter !== 'all') {
                    const cat = (e.category || 'rehab').toLowerCase();
                    if (libraryCategoryFilter === 'fat-loss') {
                      if (!(cat === 'fat-loss' || cat === 'cardio' || cat === 'workout' || cat.includes('fat') || cat.includes('cardio') || cat.includes('چربی'))) {
                        return false;
                      }
                    } else if (libraryCategoryFilter === 'rehab') {
                      if (cat === 'fat-loss' || cat === 'cardio' || cat.includes('fat') || cat.includes('cardio') || cat.includes('چربی')) {
                        return false;
                      }
                    }
                  }

                  return true;
                });

                if (filteredExercises.length === 0) {
                  return (
                    <div className="p-8 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 space-y-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {libraryFilterScope === 'personal' 
                          ? t('noPersonalExercisesYet', 'شما هنوز هیچ حرکت شخصی ایجاد نکرده‌اید. با افزودن حرکت جدید یا ایجاد نسخه شخصی از حرکات عمومی شروع کنید.')
                          : t('noExercisesInScopeCategory', 'هیچ حرکتی در این دسته‌بندی یافت نشد.')}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredExercises.map((rawEx) => {
                      const ex = rawEx;
                      const isCompleted = record.rehabCompletedIds.includes(rawEx.id);
                      const isActive = activeExerciseId === rawEx.id;
                      const isPersonal = Boolean(rawEx.user_id) || rawEx.scope === 'personal';
                      const isGlobal = !isPersonal;
                      
                      return (
                        <div 
                          key={rawEx.id}
                          className={`premium-card p-5 dark:bg-gray-800 border transition-all ${
                            isCompleted 
                              ? 'border-emerald-200 bg-emerald-50/10 dark:border-emerald-900/20' 
                              : 'hover:border-gray-300 dark:hover:border-gray-700'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <button 
                                onClick={() => toggleExerciseCompletion(rawEx.id)}
                                className={`mt-0.5 p-1 rounded-lg border transition-all ${
                                  isCompleted 
                                    ? 'bg-emerald-500 border-emerald-500 text-white shadow-sm' 
                                    : 'border-gray-300 dark:border-gray-600 hover:border-emerald-500'
                                }`}
                              >
                                <CheckCircle2 className={`w-5 h-5 ${isCompleted ? 'fill-current' : 'text-transparent'}`} />
                              </button>
                              <div>
                                <h4 className="font-semibold text-gray-900 dark:text-white font-display flex flex-wrap items-center gap-1.5 text-base">
                                  {ex.name}
                                  
                                  {/* Scope Badge: Global vs Personal */}
                                  {isGlobal ? (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 font-bold flex items-center gap-1 border border-blue-200 dark:border-blue-800" title={t('rehabScopeGlobalDesc', 'تمرین عمومی مشترک برای همه کاربران')}>
                                      <Globe className="w-3.5 h-3.5" />
                                      <span>{t('rehabScopeGlobalBadge', 'عمومی')}</span>
                                    </span>
                                  ) : (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-bold flex items-center gap-1 border border-emerald-200 dark:border-emerald-800" title={t('rehabScopePersonalDesc', 'تمرین شخصی شما')}>
                                      <User className="w-3.5 h-3.5" />
                                      <span>{t('rehabScopePersonalBadge', 'شخصی')}</span>
                                    </span>
                                  )}

                                  {/* Published to Global badge for Personal items */}
                                  {isPersonal && (rawEx.isPublishedToGlobal || rawEx.linked_global_id) && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300 font-bold flex items-center gap-1 border border-teal-200 dark:border-teal-800" title={t('publishedBadgeTooltip', 'این تمرین در بانک عمومی ثبت شده است و با هر ویرایش شما به طور خودکار همگام می‌شود.')}>
                                      <CheckCircle2 className="w-3 h-3 text-teal-600 dark:text-teal-400" />
                                      <span>{t('publishedBadge', 'منتشرشده در عمومی')}</span>
                                    </span>
                                  )}

                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 font-medium">
                                    {t('rehabCardTagRehab', 'فیزیوتراپی')}
                                  </span>

                                  {rawEx.videoUrl && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300 font-bold flex items-center gap-1">
                                      <Video className="w-3 h-3" />
                                      {t('rehabCardTagVideo', 'فیلم آموزشی')}
                                    </span>
                                  )}
                                  {rawEx.image && !rawEx.videoUrl && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 font-bold flex items-center gap-1">
                                      <Image className="w-3 h-3" />
                                      {t('rehabCardTagImage', 'عکس آموزشی')}
                                    </span>
                                  )}
                                </h4>
                                
                                {/* Description preview */}
                                <p className={`text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed ${isActive ? '' : 'line-clamp-2'}`}>
                                  {ex.description}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => {
                                  if (isGlobal) {
                                    if (confirm(t('confirmCreatePersonalCopy', 'این حرکت عمومی است و مستقیماً تغییر نمی‌کند. آیا مایلید یک نسخه شخصی از آن ایجاد شود تا تغییرات دلخواهتان را روی آن اعمال کنید؟'))) {
                                      handleCreatePersonalCopy(rawEx, true);
                                    }
                                  } else {
                                    openEditModal(rawEx);
                                  }
                                }}
                                title={isGlobal ? t('createPersonalCopyTooltip', 'ایجاد نسخه شخصی برای ویرایش') : t('rehabCardEdit', 'ویرایش و اضافه کردن فیلم و عکس')}
                                className="p-1.5 rounded-lg text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/30 transition-colors flex items-center gap-1 text-[11px] font-semibold"
                              >
                                <Paperclip className="w-4 h-4" />
                              </button>

                              {isPersonal ? (
                                <button 
                                  onClick={() => handleDeleteExercise(rawEx.id)}
                                  title={t('fatCardDelete', 'حذف تمرین')}
                                  className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 hover:text-red-600 transition-colors cursor-pointer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              ) : (
                                <span 
                                  className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 cursor-not-allowed" 
                                  title={t('globalExerciseLockedTooltip', 'حرکات عمومی محفوظ بوده و قابل حذف مستقیم نیستند')}
                                >
                                  <Lock className="w-3.5 h-3.5" />
                                </span>
                              )}

                              <button 
                                onClick={() => setActiveExerciseId(isActive ? null : rawEx.id)}
                                className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                              >
                                {isActive ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>

                          {/* Expanded View */}
                          {isActive && (
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-4 animate-fadeIn">
                              
                              {/* Full description display for full clarity */}
                              <div className="p-3 bg-gray-50/50 dark:bg-gray-900/50 rounded-xl border border-gray-100/50 dark:border-gray-800/40 text-xs text-gray-700 dark:text-gray-300 leading-relaxed flex items-start justify-between gap-2">
                                <div>
                                  <span className="text-[10px] font-bold text-gray-400 block mb-1 uppercase">{t('rehabFullInstructions', 'دستورالعمل کامل تمرین:')}</span>
                                  {ex.description}
                                </div>
                                <button
                                  onClick={() => {
                                    if (isGlobal) {
                                      if (confirm(t('createPersonalCopyConfirmPrompt', 'برای اعمال تغییرات روی این حرکت عمومی، یک نسخه شخصی ایجاد می‌شود. ادامه می‌دهید؟'))) {
                                        handleCreatePersonalCopy(rawEx, true);
                                      }
                                    } else {
                                      openEditModal(rawEx);
                                    }
                                  }}
                                  className="shrink-0 px-2.5 py-1 bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300 rounded-lg text-[11px] font-bold flex items-center gap-1 hover:bg-purple-200 transition-all cursor-pointer"
                                >
                                  <Edit3 className="w-3.5 h-3.5" />
                                  {t('rehabCardEditShort', 'ویرایش / پیوست رسانه')}
                                </button>
                              </div>

                              {/* Video / Image Media Viewer or Fallback Silhouette */}
                              <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-xl p-3 border border-gray-100/50 dark:border-gray-800/40">
                                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-2">{t('rehabMediaGuideTitle', 'راهنمای تصویری و حرکتی')}</span>
                                {rawEx.videoUrl || rawEx.image ? (
                                  <ExerciseMediaViewer exercise={rawEx} />
                                ) : (
                                  renderMinimalIllustration(rawEx.id)
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="p-2.5 bg-gray-50 dark:bg-gray-900/40 rounded-xl">
                                  <span className="font-semibold text-gray-400 block mb-0.5">{t('rehabTargetPurpose', 'هدف حرکت')}</span>
                                  <span className="text-gray-700 dark:text-gray-300">{ex.purpose}</span>
                                </div>
                                <div className="p-2.5 bg-gray-50 dark:bg-gray-900/40 rounded-xl">
                                  <span className="font-semibold text-gray-400 block mb-0.5">{t('rehabTargetMuscles', 'عضلات تحت تأثیر')}</span>
                                  <span className="text-gray-700 dark:text-gray-300">{ex.targetMuscles}</span>
                                </div>
                              </div>

                              {/* Specs Row */}
                              <div className="grid grid-cols-3 gap-2 bg-slate-50/50 dark:bg-gray-900/30 p-2.5 rounded-xl border border-gray-100 dark:border-gray-800/50 text-center text-xs">
                                <div>
                                  <span className="text-[10px] text-gray-400 block">{t('rehabCustomSets', 'تعداد ست')}</span>
                                  <span className="font-bold text-gray-800 dark:text-gray-200">{formatNumberByLang(ex.sets, lang)}</span>
                                </div>
                                <div>
                                  <span className="text-[10px] text-gray-400 block">{t('rehabCustomReps', 'تکرار / مکث')}</span>
                                  <span className="font-bold text-gray-800 dark:text-gray-200">{normalizeExerciseDigits(ex.reps, lang)}</span>
                                </div>
                                <div>
                                  <span className="text-[10px] text-gray-400 block">{t('rehabCustomRest', 'استراحت بین ست')}</span>
                                  <span className="font-bold text-gray-800 dark:text-gray-200">{normalizeExerciseDigits(ex.rest, lang)}</span>
                                </div>
                              </div>

                            </div>
                          )}

                          {/* Action Toolbar on Card */}
                          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/60 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {/* Add to my program */}
                              <button
                                type="button"
                                onClick={() => setExerciseForAddToProgram(rawEx)}
                                className="px-2.5 py-1.5 bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/40 dark:hover:bg-purple-900/60 text-purple-700 dark:text-purple-300 rounded-xl text-[11px] font-bold transition-all flex items-center gap-1 border border-purple-200 dark:border-purple-800/80 cursor-pointer shadow-2xs"
                                title={t('addToMyProgramTooltip', 'افزودن این حرکت به برنامه‌های تمرینی چندروزه شما')}
                              >
                                <PlusCircle className="w-3.5 h-3.5" />
                                <span>{t('addToMyProgram', 'افزودن به برنامه من')}</span>
                              </button>

                              {/* Edit Exercise Button */}
                              {isPersonal || isGlobalPublisherAdmin ? (
                                <button
                                  type="button"
                                  onClick={() => openEditModal(rawEx)}
                                  className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-300 rounded-xl text-[11px] font-bold transition-all flex items-center gap-1 border border-amber-200 dark:border-amber-800/80 cursor-pointer shadow-2xs"
                                  title={t('editLibraryItemTooltip', 'ویرایش عنوان، توضیحات، ست‌ها و رسانه‌های این حرکت')}
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                                  <span>{t('editLibraryItem', 'ویرایش حرکت')}</span>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (confirm(t('globalEditNotAllowedWithCopy', 'حرکات عمومی سیستم فقط توسط مدیر قابل ویرایش هستند. مایلید ابتدا نسخه شخصی این حرکت را ایجاد کنید؟'))) {
                                      handleCreatePersonalCopy(rawEx, true);
                                    }
                                  }}
                                  className="px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-[11px] font-medium transition-all flex items-center gap-1 border border-gray-200 dark:border-gray-700 cursor-pointer"
                                  title={t('editLibraryItemTooltip', 'ویرایش عنوان، توضیحات، ست‌ها و رسانه‌های این حرکت')}
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-gray-400" />
                                  <span>{t('editLibraryItem', 'ویرایش حرکت')}</span>
                                </button>
                              )}

                              {/* Create personal copy for Global exercises */}
                              {isGlobal && (
                                <button
                                  type="button"
                                  disabled={copyingExerciseId === rawEx.id}
                                  onClick={() => handleCreatePersonalCopy(rawEx)}
                                  className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/60 text-blue-700 dark:text-blue-300 rounded-xl text-[11px] font-bold transition-all flex items-center gap-1 border border-blue-200 dark:border-blue-800/80 cursor-pointer disabled:opacity-50 shadow-2xs"
                                  title={t('createPersonalCopyTooltip2', 'کپی کردن این حرکت عمومی در لیست تمرینات شخصی خود تا بتوانید آن را تغییر دهید')}
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                  <span>{copyingExerciseId === rawEx.id ? t('copying', 'در حال کپی...') : t('createPersonalCopy', 'ایجاد نسخه شخصی')}</span>
                                </button>
                              )}

                              {/* Publish to Global Library (Admin Only) */}
                              {isPersonal && isGlobalPublisherAdmin && (
                                <button
                                  type="button"
                                  disabled={publishingExerciseId === rawEx.id}
                                  onClick={() => handlePublishToGlobal(rawEx)}
                                  className={`px-2.5 py-1.5 rounded-xl text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer disabled:opacity-50 shadow-2xs ${
                                    rawEx.isPublishedToGlobal || rawEx.linked_global_id
                                      ? 'bg-teal-50 hover:bg-teal-100 dark:bg-teal-950/40 dark:hover:bg-teal-900/60 text-teal-800 dark:text-teal-200 border border-teal-300 dark:border-teal-700'
                                      : 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-900/60 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700'
                                  }`}
                                  title={
                                    rawEx.isPublishedToGlobal || rawEx.linked_global_id
                                      ? t('publishedBadgeTooltip', 'این تمرین در بانک عمومی ثبت شده است و با هر ویرایش شما به طور خودکار همگام می‌شود.')
                                      : t('publishToGlobalTooltip', 'انتشار این تمرین در بانک حرکات عمومی برای استفاده همه کاربران (مخصوص مدیریت)')
                                  }
                                >
                                  {rawEx.isPublishedToGlobal || rawEx.linked_global_id ? (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
                                  ) : (
                                    <Globe className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                                  )}
                                  <span>
                                    {publishingExerciseId === rawEx.id
                                      ? t('publishingToGlobal', 'در حال انتشار...')
                                      : rawEx.isPublishedToGlobal || rawEx.linked_global_id
                                      ? t('publishedBadge', 'همگام با بانک عمومی')
                                      : t('publishToGlobal', 'انتشار در بانک عمومی')}
                                  </span>
                                </button>
                              )}
                            </div>

                            <div className="flex items-center gap-1.5">
                              {/* View Complete Details Modal */}
                              <button
                                type="button"
                                onClick={() => setSelectedDetailExercise(rawEx)}
                                className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-[11px] font-medium transition-all flex items-center gap-1 cursor-pointer"
                                title={t('viewCompleteDetailsTooltip', 'مشاهده جزئیات کامل، فیلم، تصویر، عضلات هدف و احتیاط‌ها')}
                              >
                                <Eye className="w-3.5 h-3.5 text-gray-500" />
                                <span>{t('viewCompleteDetails', 'جزئیات کامل')}</span>
                              </button>
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}

      {/* 5. EDIT EXERCISE & ATTACH MEDIA MODAL */}
      {editingExercise && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl max-w-lg w-full p-5 space-y-4 shadow-2xl my-8">
            <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-800">
              <h3 className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-purple-600" />
                {t('rehabCardEdit', 'ویرایش و پیوست رسانه به حرکت')}
              </h3>
              <button
                onClick={() => setEditingExercise(null)}
                className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3.5 text-xs max-h-[75vh] overflow-y-auto pr-1">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300">{t('rehabCustomName', 'نام حرکت')}</label>
                  <VoiceInputButton
                    currentValue={editingExercise.name}
                    onUpdate={(val) => setEditingExercise({ ...editingExercise, name: val })}
                    title={t('voiceInputNormal', 'ورودی صوتی')}
                  />
                </div>
                <input
                  type="text"
                  value={editingExercise.name}
                  onChange={(e) => setEditingExercise({ ...editingExercise, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 font-bold"
                />
              </div>

              {/* Video Attachment Section */}
              <div className="p-3.5 bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/50 rounded-xl space-y-2.5">
                <label className="block text-xs font-bold text-purple-900 dark:text-purple-300 flex items-center gap-1.5">
                  <Video className="w-4 h-4 text-purple-600" />
                  {t('rehabCardVideoLabel', 'پیوست فیلم آموزشی (آپلود فایل یا لینک ویدیو)')}
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleVideoFileChange(e, (url) => setEditingExercise({ ...editingExercise, videoUrl: url || undefined }))}
                  className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 cursor-pointer"
                />
                <div className="flex items-center gap-1.5 pt-1">
                  <Link className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <input
                    type="text"
                    placeholder={t('rehabCustomVideoPh', 'یا لینک ویدیو (پست/ریلز اینستاگرام، آپارات، یوتیوب، MP4)...')}
                    value={editingExercise.videoUrl && !editingExercise.videoUrl.startsWith('data:') ? editingExercise.videoUrl : ''}
                    onChange={(e) => setEditingExercise({ ...editingExercise, videoUrl: e.target.value || undefined })}
                    className="w-full text-xs px-2.5 py-1.5 border border-purple-200 dark:border-purple-900 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                  />
                </div>

                {editingExercise.videoUrl && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between text-xs text-purple-700 dark:text-purple-300 font-semibold">
                      <span>{t('rehabCardVideoPreview', 'پیش‌نمایش ویدیو:')}</span>
                      <button
                        type="button"
                        onClick={() => setEditingExercise({ ...editingExercise, videoUrl: undefined })}
                        className="text-red-500 hover:underline text-[11px] font-bold cursor-pointer"
                      >
                        {t('rehabCustomVideoDel', 'حذف ویدیو')}
                      </button>
                    </div>
                    <ExerciseMediaViewer exercise={{ ...editingExercise, image: undefined }} />
                  </div>
                )}
              </div>

              {/* Image Attachment Section */}
              <div className="p-3.5 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/50 rounded-xl space-y-2.5">
                <label className="block text-xs font-bold text-emerald-900 dark:text-emerald-300 flex items-center gap-1.5">
                  <Camera className="w-4 h-4 text-emerald-600" />
                  {t('rehabCardImageLabel', 'پیوست تصویر یا گیف آموزشی')}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      compressAndSetImage(file, (dataUrl) => {
                        setEditingExercise({ ...editingExercise, image: dataUrl || undefined });
                      });
                    }
                  }}
                  className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-600 file:text-white hover:file:bg-emerald-700 cursor-pointer"
                />

                {editingExercise.image && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between text-xs text-emerald-700 dark:text-emerald-300 font-semibold">
                      <span>{t('rehabCardImagePreview', 'پیش‌نمایش تصویر:')}</span>
                      <button
                        type="button"
                        onClick={() => setEditingExercise({ ...editingExercise, image: undefined })}
                        className="text-red-500 hover:underline text-[11px] font-bold cursor-pointer"
                      >
                        {t('rehabCardImageDel', 'حذف تصویر')}
                      </button>
                    </div>
                    <img src={editingExercise.image} alt="Preview" className="max-h-40 object-contain rounded-xl border border-emerald-200 dark:border-emerald-800 mx-auto" />
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300">{t('rehabCustomDesc', 'دستورالعمل و توضیحات انجام حرکت')}</label>
                  <VoiceInputButton
                    currentValue={editingExercise.description}
                    onUpdate={(val) => setEditingExercise({ ...editingExercise, description: val })}
                    title={t('voiceInputNormal', 'ورودی صوتی')}
                  />
                </div>
                <textarea
                  rows={3}
                  value={editingExercise.description}
                  onChange={(e) => setEditingExercise({ ...editingExercise, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <label className="block text-[10px] text-gray-400">{t('rehabCustomTarget', 'عضلات هدف')}</label>
                    <VoiceInputButton
                      currentValue={editingExercise.targetMuscles}
                      onUpdate={(val) => setEditingExercise({ ...editingExercise, targetMuscles: val })}
                      title={t('voiceInputNormal', 'ورودی صوتی')}
                    />
                  </div>
                  <input
                    type="text"
                    value={editingExercise.targetMuscles}
                    onChange={(e) => setEditingExercise({ ...editingExercise, targetMuscles: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t('rehabTargetPurpose', 'هدف حرکت')}</label>
                  <input
                    type="text"
                    value={editingExercise.purpose}
                    onChange={(e) => setEditingExercise({ ...editingExercise, purpose: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t('rehabCustomSets', 'تعداد ست')}</label>
                  <input
                    type="number"
                    min={1}
                    value={editingExercise.sets ?? 3}
                    onChange={(e) => setEditingExercise({ ...editingExercise, sets: Number(e.target.value) })}
                    className="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg text-center bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t('rehabCustomReps', 'تکرار/مکث')}</label>
                  <input
                    type="text"
                    value={editingExercise.reps ?? '10'}
                    onChange={(e) => setEditingExercise({ ...editingExercise, reps: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg text-center bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t('rehabCustomRest', 'استراحت')}</label>
                  <input
                    type="text"
                    value={editingExercise.rest ?? '20'}
                    onChange={(e) => setEditingExercise({ ...editingExercise, rest: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg text-center bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t('rehabCustomDuration', 'مدت (دقیقه)')}</label>
                  <input
                    type="number"
                    min={1}
                    value={editingExercise.duration ?? 5}
                    onChange={(e) => setEditingExercise({ ...editingExercise, duration: Number(e.target.value) })}
                    className="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg text-center bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setEditingExercise(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-semibold cursor-pointer"
              >
                {t('rehabCardCancel', 'انصراف')}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!editingExercise) return;
                  
                  // Persist edit to D1 database via API
                  try {
                    const res = await apiPut(`/api/rehab/exercises/${editingExercise.id}`, {
                      name: editingExercise.name,
                      category: editingExercise.category || 'rehab',
                      description: editingExercise.description || '',
                      purpose: editingExercise.purpose || '',
                      target_muscles: editingExercise.targetMuscles || '',
                      sets: Number(editingExercise.sets || 3),
                      reps: editingExercise.reps || '10',
                      rest_time: editingExercise.rest || '20',
                      duration_minutes: Number(editingExercise.duration || 5),
                      difficulty: editingExercise.difficulty || 'Beginner',
                      image_url: editingExercise.image || null,
                      video_url: editingExercise.videoUrl || null
                    });

                    if (res && res.success && res.data) {
                      const saved: Exercise = {
                        id: res.data.id,
                        user_id: res.data.user_id,
                        scope: res.data.scope || (res.data.user_id ? 'personal' : 'global'),
                        name: res.data.name,
                        category: res.data.category || 'rehab',
                        description: res.data.description || '',
                        purpose: res.data.purpose || '',
                        targetMuscles: res.data.target_muscles || '',
                        sets: Number(res.data.sets ?? 3),
                        reps: String(res.data.reps ?? '10'),
                        rest: String(res.data.rest_time ?? '20'),
                        duration: Number(res.data.duration_minutes ?? 5),
                        difficulty: res.data.difficulty || 'Beginner',
                        image: res.data.image_url || null,
                        videoUrl: res.data.video_url || null,
                        isCustom: Boolean(res.data.is_custom || res.data.user_id),
                        source_exercise_id: res.data.source_exercise_id || null,
                        linked_global_id: res.data.linked_global_id || null,
                        isPublishedToGlobal: Boolean(res.data.is_published_to_global || res.data.linked_global_id)
                      };
                      const updated = rehabExercises.map(ex => ex.id === saved.id ? saved : ex);
                      saveRehabData(updated, rehabInbox);
                    } else {
                      const updated = rehabExercises.map(ex => ex.id === editingExercise.id ? editingExercise : ex);
                      saveRehabData(updated, rehabInbox);
                    }
                  } catch (err) {
                    console.warn('API error updating exercise:', err);
                    const updated = rehabExercises.map(ex => ex.id === editingExercise.id ? editingExercise : ex);
                    saveRehabData(updated, rehabInbox);
                  }

                  if (editingExercise.videoUrl) {
                    saveFileRecordToCloud({
                      id: `file_video_${editingExercise.id}`,
                      name: `ویدیوی ${editingExercise.name}`,
                      type: 'video',
                      section: 'rehab',
                      moduleTitle: editingExercise.name,
                      fileUrl: editingExercise.videoUrl,
                      uploadDate: new Date().toISOString(),
                      folderPath: '/storage/rehab/videos/'
                    });
                  }
                  if (editingExercise.image) {
                    saveFileRecordToCloud({
                      id: `file_img_${editingExercise.id}`,
                      name: `تصویر ${editingExercise.name}`,
                      type: 'image',
                      section: 'rehab',
                      moduleTitle: editingExercise.name,
                      fileUrl: editingExercise.image,
                      uploadDate: new Date().toISOString(),
                      folderPath: '/storage/rehab/images/'
                    });
                  }

                  refreshExercisesFromCloud();
                  setEditingExercise(null);
                }}
                className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition-all shadow-md cursor-pointer"
              >
                {t('rehabCardSave', 'ذخیره تغییرات رسانه و حرکت')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import JSON Activities Modal */}
      <BulkExerciseImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={() => {
          refreshExercisesFromCloud();
        }}
        defaultModuleId="module_1_rehab"
      />

      </>
      )}

      {/* Schedule Program Day Modal */}
      {schedulingDayInfo && (
        <ScheduleProgramDayModal
          isOpen={!!schedulingDayInfo}
          program={schedulingDayInfo.program}
          day={schedulingDayInfo.day}
          onClose={() => setSchedulingDayInfo(null)}
          onScheduled={() => {
            setSchedulingDayInfo(null);
          }}
        />
      )}

      {/* Full Exercise Detail Modal */}
      {selectedDetailExercise && (
        <ExerciseDetailModal
          isOpen={!!selectedDetailExercise}
          onClose={() => setSelectedDetailExercise(null)}
          exercise={selectedDetailExercise}
          onStartWorkout={() => {
            setRehabExercises([selectedDetailExercise]);
            setIsGuidedMode(true);
            setCurrentIdx(0);
            setSessionStatus('prepare');
            setSecondsRemaining(8);
            setIsPaused(false);
            setSelectedDetailExercise(null);
          }}
        />
      )}

      {/* Add To Multi-Day Program Modal */}
      {exerciseForAddToProgram && (
        <AddToProgramModal
          isOpen={!!exerciseForAddToProgram}
          onClose={() => setExerciseForAddToProgram(null)}
          exercise={exerciseForAddToProgram}
          programs={exercisePrograms}
          onSuccess={(updated) => {
            setExercisePrograms(updated);
            const appState = loadAppState();
            appState.settings.exercisePrograms = updated;
            saveAppState(appState, false);
            alert(t('exerciseAddedToProgramSuccess', undefined, { name: exerciseForAddToProgram?.name || '' }));
            setExerciseForAddToProgram(null);
          }}
        />
      )}

      {/* Admin Bulk Publish All to Global Modal */}
      {showPublishAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-700 space-y-5 max-h-[90vh] flex flex-col">
            
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-linear-to-tr from-amber-500 to-amber-600 flex items-center justify-center text-white shadow-xs">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    {t('publishAllConfirmTitle', 'انتشار دسته‌جمعی تمرینات در بانک عمومی')}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 font-bold border border-amber-300 dark:border-amber-700">
                      {t('adminBadge', 'مدیر ارشد')}
                    </span>
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    salim.zarepour@gmail.com
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isPublishingAll}
                onClick={() => {
                  if (!isPublishingAll) {
                    setShowPublishAllModal(false);
                    setPublishAllResults(null);
                  }
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="space-y-4 overflow-y-auto flex-1 pr-1">
              {!isPublishingAll && !publishAllResults && (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50/80 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800/60 text-xs text-amber-900 dark:text-amber-200 space-y-2 leading-relaxed">
                    <div className="font-bold flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                      <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                      <span>{t('publishAllConfirmTitle', 'انتشار دسته‌جمعی تمرینات در بانک عمومی')}</span>
                    </div>
                    <p>
                      {t('publishAllConfirmDesc', undefined, {
                        count: formatNumberByLang(rehabExercises.filter(e => Boolean(e.user_id) || e.scope === 'personal').length, lang)
                      })}
                    </p>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800 space-y-2 text-xs text-gray-600 dark:text-gray-300">
                    <div className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
                      {t('bulkPublishGuaranteesTitle', 'ویژگی‌های امن انتقال به بانک عمومی:')}
                    </div>
                    <ul className="list-disc list-inside space-y-1 text-[11px] leading-relaxed">
                      <li>{t('bulkPublishRule1', 'تمامی حرکات شخصی موجود بدون تغییر و با حفظ شناسه باقی می‌مانند.')}</li>
                      <li>{t('bulkPublishRule2', 'از ایجاد حرکات تکراری در بانک عمومی به طور خودکار جلوگیری می‌شود.')}</li>
                      <li>{t('bulkPublishRule3', 'تمام تصاویر، ویدیوها، توضیحات، ست‌ها و تکرارها با حفظ کیفیت منتقل می‌گردند.')}</li>
                      <li>{t('bulkPublishRule4', 'تمرینات شخصی با شناسه متناظر عمومی لینک شده و هرگونه ویرایش آتی به طور خودکار در بانک عمومی همگام می‌شود.')}</li>
                    </ul>
                  </div>

                  <div className="text-center text-xs text-gray-500">
                    {t('eligiblePersonalExercisesCount', 'تعداد تمرینات شخصی آماده پردازش: {{count}} مورد', {
                      count: formatNumberByLang(rehabExercises.filter(e => Boolean(e.user_id) || e.scope === 'personal').length, lang)
                    })}
                  </div>
                </div>
              )}

              {/* Processing Spinner */}
              {isPublishingAll && (
                <div className="py-12 flex flex-col items-center justify-center text-center space-y-3">
                  <div className="w-12 h-12 rounded-full border-4 border-amber-500/20 border-t-amber-600 animate-spin" />
                  <p className="text-sm font-bold text-gray-800 dark:text-gray-200">
                    {t('publishAllStatusProcessing', 'در حال پردازش و همگام‌سازی دسته‌جمعی با بانک عمومی D1...')}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t('bulkPublishWaitHint', 'لطفاً شکیبا باشید، در حال بررسی، ثبت رکوردها و پیشگیری از موارد تکراری...')}
                  </p>
                </div>
              )}

              {/* Results View */}
              {publishAllResults && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                    <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 block font-medium">{t('publishAllStatTotal', 'کل بررسی‌شده')}</span>
                      <span className="text-lg font-bold text-gray-800 dark:text-gray-200">{formatNumberByLang(publishAllResults.summary.total, lang)}</span>
                    </div>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl border border-emerald-200 dark:border-emerald-800">
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 block font-bold">{t('publishAllStatPublished', 'تازه منتشرشده')}</span>
                      <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{formatNumberByLang(publishAllResults.summary.published, lang)}</span>
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-xl border border-blue-200 dark:border-blue-800">
                      <span className="text-[10px] text-blue-600 dark:text-blue-400 block font-bold">{t('publishAllStatAlready', 'از قبل منتشرشده (همگام)')}</span>
                      <span className="text-lg font-bold text-blue-700 dark:text-blue-300">{formatNumberByLang(publishAllResults.summary.alreadyPublished, lang)}</span>
                    </div>
                    <div className="p-3 bg-red-50 dark:bg-red-950/40 rounded-xl border border-red-200 dark:border-red-800">
                      <span className="text-[10px] text-red-600 dark:text-red-400 block font-bold">{t('publishAllStatFailed', 'ناموفق')}</span>
                      <span className="text-lg font-bold text-red-700 dark:text-red-300">{formatNumberByLang(publishAllResults.summary.failed, lang)}</span>
                    </div>
                  </div>

                  {/* Scrollable Items Breakdown */}
                  <div className="space-y-1.5 max-h-56 overflow-y-auto border border-gray-100 dark:border-gray-800 rounded-xl p-2 bg-gray-50/50 dark:bg-gray-900/30 text-xs">
                    {publishAllResults.details.map((item, idx) => (
                      <div key={item.id || idx} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 truncate max-w-[240px]">
                          {item.name}
                        </span>
                        {item.status === 'published' ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-bold flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>{t('statusPublished', 'منتشر شد')}</span>
                          </span>
                        ) : item.status === 'already_published' ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 font-medium flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            <span>{t('statusAlreadyPublished', 'همگام با بانک عمومی')}</span>
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300 font-bold flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            <span>{t('statusFailed', 'خطا')}</span>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
              {!publishAllResults ? (
                <>
                  <button
                    type="button"
                    disabled={isPublishingAll}
                    onClick={() => setShowPublishAllModal(false)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    {t('cancel', 'انصراف')}
                  </button>
                  <button
                    type="button"
                    disabled={isPublishingAll || rehabExercises.filter(e => Boolean(e.user_id) || e.scope === 'personal').length === 0}
                    onClick={handlePublishAllToGlobal}
                    className="px-5 py-2 bg-linear-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-amber-200" />
                    <span>{t('startPublishAllBtn', 'شروع انتشار دسته‌جمعی')}</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setShowPublishAllModal(false);
                    setPublishAllResults(null);
                  }}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                >
                  {t('publishAllCloseBtn', 'بستن و بازخوانی')}
                </button>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
